import { useEffect, useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { Plus, Package, Search } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Modal } from "../../primitives/Modal";
import { cn } from "../../utils/cn";
import {
  listPurchaseOrders,
  getPurchaseOrder,
  listPurchaseOrderLines,
  receivePurchaseOrder,
  createPurchaseOrder,
  createPurchaseOrderLine,
  updatePurchaseOrder,
  updatePurchaseOrderLine,
  deletePurchaseOrder,
  computePurchaseOrderLines,
  type PurchaseOrderOut,
  type PurchaseOrderLineOut,
  type ConsolidatedLineOut,
} from "../../../api/purchaseOrders";
import { listSuppliers, listSupplierProducts, type Supplier, type SupplierProduct } from "../../../api/suppliers";
import { BACKEND_INVENTORY_UNAVAILABLE_MSG } from "../../../api/auth";
import { getProduct, getProductDetails, getCatalogImageUrl, type ProductDetails } from "../../../api/catalog";
import { expandKitLinesSnapshot, expandProductToKitLines, mergeKitLines } from "../../../api/kitBundleExpand";
import { exportOrderPdf, type PdfLine, type PdfKit } from "./exportOrderPdf";

/** Fetch image URL and return as data URL for embedding in PDF; returns null on failure. */
async function imageUrlToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Run async fn on items in batches to avoid too many concurrent requests (e.g. getProduct). */
async function runInBatches<T, R>(
  items: T[],
  batchSize: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    results.push(...(await Promise.all(batch.map(fn))));
  }
  return results;
}

const PRODUCT_IMAGE_BATCH_SIZE = 15;

/** Excel number format for currency (Comptabilité / Accounting style, 2 decimals, $). */
const EXCEL_CURRENCY_FORMAT = '#,##0.00 " $"';

/** Apply currency number format to given columns in a worksheet (0-based column indices). Data rows start at startRow (0-based). */
function applyCurrencyFormat(
  ws: XLSX.WorkSheet,
  currencyColIndices: number[],
  startRow: number
): void {
  const indices = Array.isArray(currencyColIndices) ? currencyColIndices : [currencyColIndices as number];
  const set = new Set(indices);
  for (const ref of Object.keys(ws)) {
    if (ref[0] === "!") continue;
    const cell = ws[ref];
    if (!cell || cell.t !== "n" || typeof cell.v !== "number") continue;
    try {
      const { r, c } = XLSX.utils.decode_cell(ref);
      if (r >= startRow && set.has(c)) (cell as XLSX.CellObject).z = EXCEL_CURRENCY_FORMAT;
    } catch {
      /* ignore */
    }
  }
}

/**
 * Apply Excel formulas to a sheet: Prix total = Qté * Coût unit. for each data row, and SUM in the total row.
 * @param ws - worksheet from aoa_to_sheet
 * @param dataStartRow - 0-based row index of first data row (after header)
 * @param numDataRows - number of data rows
 * @param colQty - 0-based column index for quantity
 * @param colUnitCost - 0-based column index for unit cost
 * @param colTotal - 0-based column index for total (will get formula Qté * Coût unit.)
 * @param sumCols - 0-based column indices to put SUM(...) in the total row (e.g. [colQty, colTotal])
 */
function applyExcelFormulas(
  ws: XLSX.WorkSheet,
  dataStartRow: number,
  numDataRows: number,
  colQty: number,
  colUnitCost: number,
  colTotal: number,
  sumCols: number[]
): void {
  const totalRow = dataStartRow + numDataRows;
  for (let i = 0; i < numDataRows; i++) {
    const r = dataStartRow + i;
    const refTotal = XLSX.utils.encode_cell({ r, c: colTotal });
    const refQty = XLSX.utils.encode_cell({ r, c: colQty });
    const refCost = XLSX.utils.encode_cell({ r, c: colUnitCost });
    ws[refTotal] = { f: `${refQty}*${refCost}`, t: "n", z: EXCEL_CURRENCY_FORMAT };
  }
  const lastDataRefRow = dataStartRow + numDataRows - 1;
  for (const c of sumCols) {
    const ref = XLSX.utils.encode_cell({ r: totalRow, c });
    const rangeStart = XLSX.utils.encode_cell({ r: dataStartRow, c });
    const rangeEnd = XLSX.utils.encode_cell({ r: lastDataRefRow, c });
    const isCurrency = c === colTotal;
    ws[ref] = { f: `SUM(${rangeStart}:${rangeEnd})`, t: "n", ...(isCurrency && { z: EXCEL_CURRENCY_FORMAT }) };
  }
}

const EXCEL_LIST_TITLE = "Liste des articles à commander";
const EXCEL_BUNDLES_REF_TITLE = "Référence — Bundles (non inclus dans les totaux ci-dessus)";
const EXCEL_PART_BUNDLE_COL = "Part / Bundle";
const EXCEL_ITEM_NAME_COL = "Nom article";

function catalogNameFromDetails(d: ProductDetails & { product?: { name?: string } }): string {
  return (d.product?.name ?? d.name ?? "").trim();
}

function buildNameBySkuFromSupplierProducts(products: SupplierProduct[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const sp of products) {
    const sku = sp.supplier_sku.trim();
    if (!sku) continue;
    const name = (sp.product_name || sp.supplier_product_name || "").trim();
    if (name) map.set(sku, name);
  }
  return map;
}

function excelSupplierLineName(
  line: { description?: string | null; supplier_sku?: string },
  nameBySku: Map<string, string>
): string {
  const fromDesc = (line.description ?? "").trim();
  if (fromDesc) return fromDesc;
  const sku = (line.supplier_sku ?? "").trim();
  if (sku && nameBySku.has(sku)) return nameBySku.get(sku)!;
  return "—";
}

function excelCatalogLineName(
  productId: string | undefined,
  nameByProduct: Record<string, string>,
  line?: { description?: string | null; supplier_sku?: string },
  nameBySku?: Map<string, string>
): string {
  if (productId) {
    const fromCatalog = (nameByProduct[productId] ?? "").trim();
    if (fromCatalog) return fromCatalog;
  }
  if (line && nameBySku) {
    const fallback = excelSupplierLineName(line, nameBySku);
    if (fallback !== "—") return fallback;
  }
  return "—";
}

async function fetchSellingAndNamesByProduct(
  productIds: string[]
): Promise<{ sellingByProduct: Record<string, number>; nameByProduct: Record<string, string> }> {
  const sellingByProduct: Record<string, number> = {};
  const nameByProduct: Record<string, string> = {};
  await Promise.all(
    productIds.map((id) =>
      getProductDetails(id)
        .then((d) => {
          const price = d.item_prices?.[0]?.unit_price;
          if (price != null) sellingByProduct[id] = price;
          const name = catalogNameFromDetails(d);
          if (name) nameByProduct[id] = name;
        })
        .catch(() => {})
    )
  );
  return { sellingByProduct, nameByProduct };
}

function formatOrderPrice(value: number | undefined | null, currency = "CAD"): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(2)} ${currency}`;
}

/** Pour des lignes export (non-bundle), construit une map supplier_sku -> "Part" ou SKU du bundle ou "Part; SKU". */
function buildPartOrBundleBySku<T extends { supplier_sku?: string; parent_bundle_id?: string | null }>(
  lines: T[],
  bundleSkuByProductId: Map<string, string>
): Map<string, string> {
  const bySku = new Map<string, { part: boolean; bundleSkus: Set<string> }>();
  for (const l of lines) {
    const sku = (l.supplier_sku ?? "").trim() || "—";
    if (!bySku.has(sku)) bySku.set(sku, { part: false, bundleSkus: new Set() });
    const entry = bySku.get(sku)!;
    if (!l.parent_bundle_id) entry.part = true;
    else {
      const bSku = bundleSkuByProductId.get(l.parent_bundle_id);
      if (bSku) entry.bundleSkus.add(bSku);
    }
  }
  const result = new Map<string, string>();
  bySku.forEach((entry, sku) => {
    const partOnly = entry.part && entry.bundleSkus.size === 0;
    const bundleOnly = !entry.part && entry.bundleSkus.size > 0;
    if (partOnly) result.set(sku, "Part");
    else if (bundleOnly) result.set(sku, [...entry.bundleSkus].join(", "));
    else result.set(sku, "Part; " + [...entry.bundleSkus].join(", "));
  });
  return result;
}

/** Retourne les lignes à ajouter sous le tableau pour lister les bundles en référence (une ligne par bundle, sans impact sur les totaux). */
function excelBundleReferenceRows<T extends { product_id: string; supplier_sku: string; description?: string | null; is_bundle?: boolean; qty_ordered?: number; qty?: number; unit_cost?: number; currency?: string }>(
  lines: T[],
  qtyField: "qty_ordered" | "qty" = "qty_ordered",
  nameByProduct: Record<string, string> = {},
  nameBySku: Map<string, string> = new Map()
): (string | number)[][] {
  const bundles = lines.filter((l) => l.is_bundle);
  if (bundles.length === 0) return [];
  const byProductId = new Map<string, T>();
  for (const b of bundles) {
    if (!byProductId.has(b.product_id)) byProductId.set(b.product_id, b);
  }
  const bundleList = Array.from(byProductId.values());
  const qty = (l: T) => (qtyField === "qty_ordered" ? l.qty_ordered : l.qty) ?? 0;
  return [
    [],
    [EXCEL_BUNDLES_REF_TITLE],
    ["Nom", "SKU", EXCEL_ITEM_NAME_COL, "Qté", "Coût unit.", "Devise", "Remarque"],
    ...bundleList.map((b) => [
      excelSupplierLineName(b, nameBySku),
      b.supplier_sku,
      excelCatalogLineName(b.product_id, nameByProduct, b, nameBySku),
      qty(b),
      b.unit_cost ?? "",
      b.currency ?? "CAD",
      "en référence",
    ]),
  ];
}

/** Regroupe les lignes par SKU fournisseur et somme les quantités (une seule ligne par SKU). */
function groupExportLinesBySku<T extends { supplier_sku: string; qty_ordered?: number; qty?: number; unit_cost?: number; currency?: string; description?: string | null; product_id?: string; qty_received?: number }>(
  lines: T[],
  qtyField: "qty_ordered" | "qty" = "qty_ordered"
): T[] {
  const bySku = new Map<string, T & { qty_ordered?: number; qty?: number; qty_received?: number }>();
  for (const l of lines) {
    const sku = l.supplier_sku?.trim() || "";
    const qty = (qtyField === "qty_ordered" ? l.qty_ordered : l.qty) ?? 0;
    const recv = l.qty_received ?? 0;
    const existing = bySku.get(sku);
    if (!existing) {
      bySku.set(sku, { ...l, [qtyField]: qty, qty_received: recv });
    } else {
      (existing as Record<string, number>)[qtyField] = ((existing as Record<string, number>)[qtyField] ?? 0) + qty;
      (existing as Record<string, number>).qty_received = ((existing as Record<string, number>).qty_received ?? 0) + recv;
    }
  }
  return Array.from(bySku.values()) as T[];
}

type OrderRow = {
  id: string;
  po_number: string;
  supplier: string;
  status: string;
  order_date: string | null;
  expected_date: string | null;
  total_lines: number;
  remaining_to_receive: number;
  value_ordered: number;
  value_received: number;
  value_remaining: number;
};

type InventoryCommandesProps = {
  selectedOrderId: string | null;
};

export function InventoryCommandes({ selectedOrderId }: InventoryCommandesProps) {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [panelOrderId, setPanelOrderId] = useState<string | null>(selectedOrderId);
  const [poDetail, setPoDetail] = useState<PurchaseOrderOut | null>(null);
  const [poLines, setPoLines] = useState<PurchaseOrderLineOut[]>([]);
  /** Expanded lines (bundle + components) for display when we have composition_snapshot; null otherwise. */
  const [expandedDisplayLines, setExpandedDisplayLines] = useState<(ConsolidatedLineOut & { qty_received?: number })[] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveQtyByLineId, setReceiveQtyByLineId] = useState<Record<string, number>>({});
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [exportingOrder, setExportingOrder] = useState(false);
  const [exportingOrderPdf, setExportingOrderPdf] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [sellingByProduct, setSellingByProduct] = useState<Record<string, number>>({});

  const selectedOrder = useMemo(() => orders.find((o) => o.id === panelOrderId), [orders, panelOrderId]);

  const exportOrderExcel = useCallback(async () => {
    if (!poDetail || !poLines.length) return;
    setExportingOrder(true);
    try {
      const supplierName = selectedOrder?.supplier ?? "Fournisseur";
      const snapshot = poDetail.composition_snapshot;
      const supplierId = poDetail.supplier_id;

      type ExportLine = {
        product_id: string;
        supplier_sku: string;
        description: string | null;
        qty_ordered: number;
        qty_received?: number;
        unit_cost: number;
        currency: string;
        sources?: string[];
        is_bundle?: boolean;
        parent_bundle_id?: string | null;
      };
      let exportLines: ExportLine[] = poLines.map((l) => ({
        product_id: l.product_id,
        supplier_sku: l.supplier_sku,
        description: l.description,
        qty_ordered: l.qty_ordered,
        qty_received: l.qty_received,
        unit_cost: l.unit_cost,
        currency: l.currency,
      }));
      if (snapshot?.kits?.length && supplierId) {
        try {
          const computed = await computePurchaseOrderLines({
            supplier_id: supplierId,
            kits: snapshot.kits.map((k: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }) => ({
              name: k.name,
              quantity: k.quantity,
              lines: k.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })),
            })),
          });
          exportLines = computed.consolidated_lines.map((l) => ({
            product_id: l.product_id,
            supplier_sku: l.supplier_sku,
            description: l.description,
            qty_ordered: l.qty,
            unit_cost: l.unit_cost,
            currency: l.currency,
            sources: l.sources,
            is_bundle: l.is_bundle,
            parent_bundle_id: l.parent_bundle_id,
          }));
        } catch {
          // Fallback: use poLines as-is
        }
      }

      const uniqueIds = [...new Set(exportLines.map((l) => l.product_id))];
      const [{ sellingByProduct, nameByProduct }, supplierProductsForExport] = await Promise.all([
        fetchSellingAndNamesByProduct(uniqueIds),
        supplierId ? listSupplierProducts(supplierId).catch(() => [] as SupplierProduct[]) : Promise.resolve([] as SupplierProduct[]),
      ]);
      const nameBySku = buildNameBySkuFromSupplierProducts(supplierProductsForExport);
      const lineByProduct = new Map(poLines.map((l) => [l.product_id, l]));
      const wb = XLSX.utils.book_new();
      const safeName = (s: string) => (s.length > 31 ? s.slice(0, 31) : s).replace(/[*?:/\\[\]]/g, " ");
      const bundleSkuByProductId = new Map<string, string>();
      exportLines.forEach((l) => {
        if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
      });
      const productByProductId = new Map(exportLines.map((l) => [l.product_id, l]));
      const orderLinesOnly = exportLines.filter((l) => !l.parent_bundle_id);
      const table1Lines = groupExportLinesBySku(
        exportLines.filter((l) => !l.is_bundle),
        "qty_ordered"
      );
      const partOrBundleBySku = buildPartOrBundleBySku(
        exportLines.filter((l) => !l.is_bundle),
        bundleSkuByProductId
      );
      const h1 = ["Nom", "SKU", EXCEL_ITEM_NAME_COL, EXCEL_PART_BUNDLE_COL, "Qté commandée", "Qté reçue", "Coût unit.", "Prix détail", "Prix total", "Devise"];
      const dataStartRowCmd = 8;
      const rows1 = table1Lines.map((l) => {
        const received = l.qty_received ?? lineByProduct.get(l.product_id)?.qty_received ?? 0;
        const prixDetail = sellingByProduct[l.product_id];
        const partOrBundle = partOrBundleBySku.get((l.supplier_sku ?? "").trim()) ?? "Part";
        return [
          excelSupplierLineName(l, nameBySku),
          l.supplier_sku,
          excelCatalogLineName(l.product_id, nameByProduct, l, nameBySku),
          partOrBundle,
          l.qty_ordered,
          received,
          l.unit_cost ?? "",
          typeof prixDetail === "number" && !Number.isNaN(prixDetail) ? prixDetail : "",
          0,
          l.currency,
        ];
      });
      const totalRow1 = ["TOTAL", "", "", "", "", "", "", "", 0, ""];
      const sheetRows: (string | number)[][] = [
        ["Fournisseur", supplierName],
        ["Référence", poDetail.supplier_reference ?? ""],
        ["Date commande", poDetail.order_date ?? ""],
        ["Statut", poDetail.status],
        [],
        [EXCEL_LIST_TITLE],
        [],
        h1,
        ...rows1,
        totalRow1,
        ...excelBundleReferenceRows(exportLines, "qty_ordered", nameByProduct, nameBySku),
      ];
      const ws = XLSX.utils.aoa_to_sheet(sheetRows);
      applyCurrencyFormat(ws, [6, 7, 8], dataStartRowCmd);
      applyExcelFormulas(ws, dataStartRowCmd, table1Lines.length, 4, 6, 8, [4, 5, 8]);
      XLSX.utils.book_append_sheet(wb, ws, safeName("COMMANDE"));
      if (snapshot?.kits?.length) {
        for (const kit: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] } of snapshot.kits) {
          const computed = await computePurchaseOrderLines({
            supplier_id: supplierId,
            kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
          });
          type KitExportLine = { product_id: string; supplier_sku: string; description: string | null; qty: number; unit_cost: number; currency: string; is_bundle?: boolean; parent_bundle_id?: string | null };
          const kitLines: KitExportLine[] = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
            const base = productByProductId.get(l.product_id);
            return {
              product_id: l.product_id,
              supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
              description: l.description ?? base?.description ?? null,
              qty: l.qty,
              unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
              currency: l.currency ?? base?.currency ?? "CAD",
              is_bundle: l.is_bundle,
              parent_bundle_id: l.parent_bundle_id ?? undefined,
            };
          });
          const kitBundleSkuByProductId = new Map<string, string>();
          kitLines.forEach((l) => {
            if (l.is_bundle) kitBundleSkuByProductId.set(l.product_id, l.supplier_sku);
          });
          const kitPartOrBundleBySku = buildPartOrBundleBySku(
            kitLines.filter((l) => !l.is_bundle),
            kitBundleSkuByProductId
          );
          const kTable1 = groupExportLinesBySku(kitLines.filter((l) => !l.is_bundle), "qty");
          const kitDataStartRow = 6;
          const kitSheetRows: (string | number)[][] = [
            ["Kit", kit.name],
            ["Quantité assemblages", kit.quantity],
            [],
            [EXCEL_LIST_TITLE],
            [],
            ["Nom", "SKU", EXCEL_ITEM_NAME_COL, EXCEL_PART_BUNDLE_COL, "Qté/kit ou Total", "Coût unit.", "Prix total", "Devise"],
            ...kTable1.map((l) => {
              const partOrBundle = kitPartOrBundleBySku.get((l.supplier_sku ?? "").trim()) ?? "Part";
              return [
                excelSupplierLineName(l, nameBySku),
                l.supplier_sku,
                excelCatalogLineName(l.product_id, nameByProduct, l, nameBySku),
                partOrBundle,
                l.qty,
                l.unit_cost ?? "",
                0,
                l.currency,
              ];
            }),
            ["TOTAL", "", "", "", 0, "", 0, ""],
            ...excelBundleReferenceRows(kitLines, "qty", nameByProduct, nameBySku),
          ];
          const wsKit = XLSX.utils.aoa_to_sheet(kitSheetRows);
          applyCurrencyFormat(wsKit, [5, 6], kitDataStartRow);
          applyExcelFormulas(wsKit, kitDataStartRow, kTable1.length, 4, 5, 6, [4, 6]);
          XLSX.utils.book_append_sheet(wb, wsKit, safeName(kit.name || "Kit"));
        }
      }
      const filename = `commande_${supplierName.replace(/\s+/g, "_")}_${poDetail.supplier_reference || poDetail.id.slice(0, 8)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } finally {
      setExportingOrder(false);
    }
  }, [poDetail, poLines, selectedOrder?.supplier]);

  const handleExportOrderPdf = useCallback(async () => {
    if (!poDetail || !poLines.length) return;
    setExportingOrderPdf(true);
    try {
      const supplierName = selectedOrder?.supplier ?? "Fournisseur";
      const snapshot = poDetail.composition_snapshot;
      const supplierId = poDetail.supplier_id;
      type ExportLine = {
        description: string | null;
        supplier_sku: string;
        qty_ordered: number;
        unit_cost: number;
        currency: string;
        sources?: string[];
        is_bundle?: boolean;
        parent_bundle_id?: string | null;
        product_id?: string;
      };
      let exportLines: ExportLine[] = poLines.map((l) => ({
        description: l.description,
        supplier_sku: l.supplier_sku,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency,
        product_id: l.product_id,
      }));
      const bundleSkuByProductId = new Map<string, string>();
      if (snapshot?.kits?.length && supplierId) {
        try {
          const computed = await computePurchaseOrderLines({
            supplier_id: supplierId,
            kits: snapshot.kits.map((k: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }) => ({
              name: k.name,
              quantity: k.quantity,
              lines: k.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })),
            })),
          });
          computed.consolidated_lines.forEach((l) => {
            if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
          });
          exportLines = computed.consolidated_lines.map((l) => ({
            description: l.description,
            supplier_sku: l.supplier_sku,
            qty_ordered: l.qty,
            unit_cost: l.unit_cost,
            currency: l.currency,
            sources: l.sources,
            is_bundle: l.is_bundle,
            parent_bundle_id: l.parent_bundle_id,
            product_id: l.product_id,
          }));
        } catch {
          // use poLines as-is
        }
      }
      // PDF page Commande complète : toutes les lignes (bundle + composants) pour affichage ; bundle en gras, composants en gris
      const productByProductIdPdf = new Map(exportLines.map((l) => [l.product_id, l]));
      const pdfLines: PdfLine[] = exportLines.map((l) => ({
        description: l.description,
        supplier_sku: l.supplier_sku,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency,
        is_bundle: l.is_bundle,
        parent_bundle_sku: l.parent_bundle_id ? (bundleSkuByProductId.get(l.parent_bundle_id) ?? null) : null,
      }));
      // PDF kits = même affichage que la page commande complète : bundle + composants avec Type et style
      const pdfKits: PdfKit[] | undefined = snapshot?.kits?.length
        ? await Promise.all(
            snapshot.kits.map(async (kit: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }) => {
              const computed = await computePurchaseOrderLines({
                supplier_id: supplierId,
                kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
              });
              const kitBundleSku = new Map<string, string>();
              computed.consolidated_lines.forEach((l: { is_bundle?: boolean; product_id: string; supplier_sku: string }) => {
                if (l.is_bundle) kitBundleSku.set(l.product_id, l.supplier_sku);
              });
              const lines: PdfLine[] = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
                const base = productByProductIdPdf.get(l.product_id);
                return {
                  description: l.description ?? base?.description ?? null,
                  supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
                  qty_ordered: l.qty,
                  unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
                  currency: l.currency ?? base?.currency ?? "CAD",
                  qty_for_this_kit: l.qty,
                  is_bundle: l.is_bundle,
                  parent_bundle_sku: l.parent_bundle_id ? (kitBundleSku.get(l.parent_bundle_id) ?? null) : null,
                };
              });
              return { name: kit.name, quantity: kit.quantity, lines };
            })
          )
        : undefined;
      exportOrderPdf({
        supplierName,
        supplierReference: poDetail.supplier_reference ?? undefined,
        orderDate: poDetail.order_date ?? undefined,
        expectedDate: poDetail.expected_date ?? undefined,
        status: poDetail.status,
        notes: poDetail.notes ?? undefined,
        lines: pdfLines,
        kits: pdfKits,
        filename: `commande_${supplierName.replace(/\s+/g, "_")}_${poDetail.supplier_reference || poDetail.id.slice(0, 8)}.pdf`,
      });
    } finally {
      setExportingOrderPdf(false);
    }
  }, [poDetail, poLines, selectedOrder?.supplier]);

  const handleExportOrderPdfWithImages = useCallback(async () => {
    if (!poDetail || !poLines.length) return;
    setExportingOrderPdf(true);
    try {
      const supplierName = selectedOrder?.supplier ?? "Fournisseur";
      const snapshot = poDetail.composition_snapshot;
      const supplierId = poDetail.supplier_id;
      type ExportLine = {
        description: string | null;
        supplier_sku: string;
        qty_ordered: number;
        unit_cost: number;
        currency: string;
        sources?: string[];
        is_bundle?: boolean;
        parent_bundle_id?: string | null;
        product_id?: string;
      };
      let exportLines: ExportLine[] = poLines.map((l) => ({
        description: l.description,
        supplier_sku: l.supplier_sku,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency,
        product_id: l.product_id,
      }));
      const bundleSkuByProductId = new Map<string, string>();
      if (snapshot?.kits?.length && supplierId) {
        try {
          const computed = await computePurchaseOrderLines({
            supplier_id: supplierId,
            kits: snapshot.kits.map((k: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }) => ({
              name: k.name,
              quantity: k.quantity,
              lines: k.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })),
            })),
          });
          computed.consolidated_lines.forEach((l) => {
            if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
          });
          exportLines = computed.consolidated_lines.map((l) => ({
            description: l.description,
            supplier_sku: l.supplier_sku,
            qty_ordered: l.qty,
            unit_cost: l.unit_cost,
            currency: l.currency,
            sources: l.sources,
            is_bundle: l.is_bundle,
            parent_bundle_id: l.parent_bundle_id,
            product_id: l.product_id,
          }));
        } catch {
          // use poLines as-is
        }
      }
      const uniqueProductIds = [...new Set(exportLines.map((l) => l.product_id).filter(Boolean))] as string[];
      const imageMap = new Map<string, string | null>();
      for (const productId of uniqueProductIds) {
        try {
          const product = await getProduct(productId);
          const path = (product as { image_path?: string | null }).image_path;
          if (path) {
            const dataUrl = await imageUrlToDataUrl(getCatalogImageUrl(path));
            imageMap.set(productId, dataUrl);
          } else {
            imageMap.set(productId, null);
          }
        } catch {
          imageMap.set(productId, null);
        }
      }
      const productByProductIdPdfImg = new Map(exportLines.map((l) => [l.product_id, l]));
      const pdfLines: PdfLine[] = exportLines.map((l) => ({
        description: l.description,
        supplier_sku: l.supplier_sku,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency,
        is_bundle: l.is_bundle,
        parent_bundle_sku: l.parent_bundle_id ? (bundleSkuByProductId.get(l.parent_bundle_id) ?? null) : null,
        imageDataUrl: l.product_id ? (imageMap.get(l.product_id) ?? null) : null,
      }));
      const pdfKits: PdfKit[] | undefined = snapshot?.kits?.length
        ? await Promise.all(
            snapshot.kits.map(async (kit: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }) => {
              const computed = await computePurchaseOrderLines({
                supplier_id: supplierId,
                kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll: { product_id: string; qty_per_kit: number }) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
              });
              const kitBundleSku = new Map<string, string>();
              computed.consolidated_lines.forEach((l: { is_bundle?: boolean; product_id: string; supplier_sku: string }) => {
                if (l.is_bundle) kitBundleSku.set(l.product_id, l.supplier_sku);
              });
              const lines: PdfLine[] = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
                const base = productByProductIdPdfImg.get(l.product_id);
                return {
                  description: l.description ?? base?.description ?? null,
                  supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
                  qty_ordered: l.qty,
                  unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
                  currency: l.currency ?? base?.currency ?? "CAD",
                  qty_for_this_kit: l.qty,
                  is_bundle: l.is_bundle,
                  parent_bundle_sku: l.parent_bundle_id ? (kitBundleSku.get(l.parent_bundle_id) ?? null) : null,
                  imageDataUrl: imageMap.get(l.product_id) ?? null,
                };
              });
              return { name: kit.name, quantity: kit.quantity, lines };
            })
          )
        : undefined;
      const baseName = `commande_${supplierName.replace(/\s+/g, "_")}_${poDetail.supplier_reference || poDetail.id.slice(0, 8)}`;
      exportOrderPdf({
        supplierName,
        supplierReference: poDetail.supplier_reference ?? undefined,
        orderDate: poDetail.order_date ?? undefined,
        expectedDate: poDetail.expected_date ?? undefined,
        status: poDetail.status,
        notes: poDetail.notes ?? undefined,
        lines: pdfLines,
        kits: pdfKits,
        withImages: true,
        filename: `${baseName}_avec_images.pdf`,
      });
    } finally {
      setExportingOrderPdf(false);
    }
  }, [poDetail, poLines, selectedOrder?.supplier]);

  useEffect(() => {
    setPanelOrderId(selectedOrderId);
  }, [selectedOrderId]);

  const refreshOrders = useCallback(async () => {
    const [poList, suppliers] = await Promise.all([
      listPurchaseOrders({ page_size: 100 }),
      listSuppliers({ page_size: 500 }),
    ]);
    const supplierMap: Record<string, string> = {};
    suppliers.forEach((s) => {
      supplierMap[s.id] = s.name;
    });
    const linesByPo = await Promise.all(
      poList.map((po) => listPurchaseOrderLines(po.id).catch(() => [] as PurchaseOrderLineOut[]))
    );
    const rows: OrderRow[] = poList.map((po, i) => {
      const lines = linesByPo[i] || [];
      let value_ordered = 0;
      let value_received = 0;
      lines.forEach((l) => {
        value_ordered += l.qty_ordered * (l.unit_cost ?? 0);
        value_received += (l.qty_received ?? 0) * (l.unit_cost ?? 0);
      });
      const remaining_to_receive = lines.reduce((sum, l) => sum + Math.max(0, l.qty_ordered - (l.qty_received ?? 0)), 0);
      return {
        id: po.id,
        po_number: po.supplier_reference || `PO-${po.id.slice(0, 8)}`,
        supplier: supplierMap[po.supplier_id] || po.supplier_id.slice(0, 8),
        status: po.status,
        order_date: po.order_date,
        expected_date: po.expected_date,
        total_lines: lines.length,
        remaining_to_receive,
        value_ordered,
        value_received,
        value_remaining: value_ordered - value_received,
      };
    });
    setOrders(rows);
    setModuleError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refreshOrders()
      .catch((err) => {
        if (!cancelled) {
          setOrders([]);
          setModuleError(err instanceof Error ? err.message : null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshOrders]);

  const loadDetail = useCallback(async (poId: string) => {
    setDetailLoading(true);
    try {
      const [po, lines] = await Promise.all([
        getPurchaseOrder(poId),
        listPurchaseOrderLines(poId),
      ]);
      setPoDetail(po);
      setPoLines(lines);
      setReceiveQtyByLineId({});
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCancelOrder = useCallback(async () => {
    if (!panelOrderId || !window.confirm("Êtes-vous sûr de vouloir annuler cette commande ? Elle passera au statut « Annulée ».")) return;
    setCancellingOrder(true);
    try {
      await updatePurchaseOrder(panelOrderId, { status: "cancelled" });
      await refreshOrders();
      await loadDetail(panelOrderId);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Erreur lors de l'annulation.");
    } finally {
      setCancellingOrder(false);
    }
  }, [panelOrderId, refreshOrders, loadDetail]);

  const handleDeleteOrder = useCallback(async () => {
    if (!panelOrderId || !window.confirm("Êtes-vous sûr de vouloir supprimer définitivement cette commande ? Cette action est irréversible. Les lignes de la commande seront également supprimées.")) return;
    setDeletingOrder(true);
    try {
      await deletePurchaseOrder(panelOrderId);
      setPanelOrderId(null);
      await refreshOrders();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Erreur lors de la suppression.");
    } finally {
      setDeletingOrder(false);
    }
  }, [panelOrderId, refreshOrders]);

  useEffect(() => {
    if (panelOrderId) {
      loadDetail(panelOrderId);
    } else {
      setPoDetail(null);
      setPoLines([]);
      setExpandedDisplayLines(null);
    }
  }, [panelOrderId, loadDetail]);

  // When we have a composition snapshot, recompute consolidated lines (bundles → pièces) for display
  useEffect(() => {
    if (!poDetail?.composition_snapshot?.kits?.length || !poDetail.supplier_id) {
      setExpandedDisplayLines(null);
      return;
    }
    let cancelled = false;
    computePurchaseOrderLines({
      supplier_id: poDetail.supplier_id,
      kits: poDetail.composition_snapshot.kits.map((k) => ({
        name: k.name,
        quantity: k.quantity,
        lines: k.lines.map((ll) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })),
      })),
    })
      .then((res) => {
        if (cancelled) return;
        const lineByProduct = new Map(poLines.map((l) => [l.product_id, l]));
        setExpandedDisplayLines(
          res.consolidated_lines.map((l) => ({
            ...l,
            qty_received: lineByProduct.get(l.product_id)?.qty_received,
          }))
        );
      })
      .catch(() => setExpandedDisplayLines(null));
    return () => {
      cancelled = true;
    };
  }, [poDetail?.id, poDetail?.supplier_id, poDetail?.composition_snapshot, poLines]);

  useEffect(() => {
    const ids = new Set<string>();
    poLines.forEach((l) => l.product_id && ids.add(l.product_id));
    (expandedDisplayLines ?? []).forEach((l) => l.product_id && ids.add(l.product_id));
    poDetail?.composition_snapshot?.kits?.forEach((k) =>
      k.lines.forEach((ll) => ll.product_id && ids.add(ll.product_id))
    );
    const uniqueIds = [...ids];
    if (uniqueIds.length === 0) {
      setSellingByProduct({});
      return;
    }
    let cancelled = false;
    fetchSellingAndNamesByProduct(uniqueIds).then(({ sellingByProduct: prices }) => {
      if (!cancelled) setSellingByProduct(prices);
    });
    return () => {
      cancelled = true;
    };
  }, [poLines, expandedDisplayLines, poDetail?.composition_snapshot]);

  const filtered = orders.filter(
    (o) =>
      !search ||
      o.po_number.toLowerCase().includes(search.toLowerCase()) ||
      o.supplier.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-1 min-h-0 w-full">
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <div className="p-6 space-y-4 min-w-0 flex-1 flex flex-col">
          {moduleError === BACKEND_INVENTORY_UNAVAILABLE_MSG && (
            <div className="rounded-md border border-warning bg-warning/10 p-4 text-sm text-warning">
              {moduleError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-xl font-semibold text-text">Commandes fournisseurs</h1>
            <Button
              label="Créer une commande"
              intent="primary"
              size="sm"
              onClick={() => setCreateModalOpen(true)}
            />
          </div>

          <div className="min-w-[240px]">
            <Input
              label="Recherche"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Numéro PO, fournisseur..."
            />
          </div>

          {loading ? (
            <p className="text-sm text-muted">Chargement...</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border min-w-0 flex-1">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-muted">
                    <th className="text-left p-3 font-medium text-text">Numéro PO</th>
                    <th className="text-left p-3 font-medium text-text">Fournisseur</th>
                    <th className="text-left p-3 font-medium text-text">Statut</th>
                    <th className="text-left p-3 font-medium text-text">Date commande</th>
                    <th className="text-left p-3 font-medium text-text">Date prévue</th>
                    <th className="text-right p-3 font-medium text-text">Lignes</th>
                    <th className="text-right p-3 font-medium text-text">Restant (qté)</th>
                    <th className="text-right p-3 font-medium text-text">Montant commandé</th>
                    <th className="text-right p-3 font-medium text-text">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-6 text-center text-muted">
                        Aucune commande. Créez une commande pour commencer.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((o) => (
                      <tr
                        key={o.id}
                        className={cn(
                          "border-b border-border hover:bg-surface-muted/50 cursor-pointer",
                          panelOrderId === o.id && "bg-primary/5"
                        )}
                        onClick={() => {
                          setPanelOrderId(o.id);
                          window.location.hash = `#/inventories/commandes/${o.id}`;
                        }}
                      >
                        <td className="p-3 font-medium text-text">{o.po_number}</td>
                        <td className="p-3">{o.supplier}</td>
                        <td className="p-3">{o.status}</td>
                        <td className="p-3 text-muted">{o.order_date ? new Date(o.order_date).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-muted">{o.expected_date ? new Date(o.expected_date).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-right">{o.total_lines}</td>
                        <td className="p-3 text-right">{o.remaining_to_receive}</td>
                        <td className="p-3 text-right">{typeof o.value_ordered === "number" ? o.value_ordered.toFixed(2) : "—"}</td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            className="text-primary hover:underline text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPanelOrderId(o.id);
                              window.location.hash = `#/inventories/commandes/${o.id}`;
                            }}
                          >
                            Détail
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {panelOrderId && (
        <div className="flex-shrink-0 w-full sm:w-[400px] xl:w-[440px] border-l border-border bg-surface flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
            <h2 className="font-semibold text-text truncate">Détail commande</h2>
            <button
              type="button"
              className="text-muted hover:text-text text-sm"
              onClick={() => {
                setPanelOrderId(null);
                window.location.hash = "#/inventories/commandes";
              }}
            >
              Fermer
            </button>
          </div>
          <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4 space-y-4 w-full">
            {detailLoading ? (
              <p className="text-sm text-muted">Chargement...</p>
            ) : poDetail ? (
              <>
                <div className="text-sm space-y-1 w-full min-w-0">
                  <p><span className="text-muted">Statut :</span> {poDetail.status}</p>
                  {poDetail.order_date && (
                    <p><span className="text-muted">Date commande :</span> {new Date(poDetail.order_date).toLocaleDateString()}</p>
                  )}
                  {poDetail.expected_date && (
                    <p><span className="text-muted">Date prévue :</span> {new Date(poDetail.expected_date).toLocaleDateString()}</p>
                  )}
                  {poDetail.notes && <p className="text-muted">{poDetail.notes}</p>}
                </div>
                <div className="flex flex-wrap gap-2 mb-3 w-full min-w-0">
                  <Button
                    label={exportingOrder ? "Export…" : "Exporter Excel"}
                    intent="default"
                    size="sm"
                    onClick={() => exportOrderExcel()}
                    disabled={poLines.length === 0 || exportingOrder}
                  />
                  <Button
                    label={exportingOrderPdf ? "Export…" : "Exporter PDF"}
                    intent="default"
                    size="sm"
                    onClick={() => handleExportOrderPdf()}
                    disabled={poLines.length === 0 || exportingOrderPdf}
                  />
                  <Button
                    label={exportingOrderPdf ? "Export…" : "Exporter PDF (avec images)"}
                    intent="default"
                    size="sm"
                    onClick={() => handleExportOrderPdfWithImages()}
                    disabled={poLines.length === 0 || exportingOrderPdf}
                  />
                  <Button
                    label="Modifier"
                    intent="default"
                    size="sm"
                    onClick={() => setEditModalOpen(true)}
                  />
                  {(poDetail.status === "sent" || poDetail.status === "partially_received") && (
                    <Button
                      label="Réceptionner"
                      intent="primary"
                      size="sm"
                      onClick={() => setReceiveModalOpen(true)}
                    />
                  )}
                  {poDetail.status !== "cancelled" && (
                    <Button
                      label={cancellingOrder ? "Annulation…" : "Annuler la commande"}
                      intent="default"
                      size="sm"
                      state={cancellingOrder || deletingOrder ? "disabled" : "default"}
                      onClick={() => handleCancelOrder()}
                    />
                  )}
                  <Button
                    label={deletingOrder ? "Suppression…" : "Supprimer définitivement"}
                    intent="danger"
                    size="sm"
                    state={cancellingOrder || deletingOrder ? "disabled" : "default"}
                    onClick={() => handleDeleteOrder()}
                  />
                </div>
                {poDetail.composition_snapshot?.kits?.length ? (
                  <div className="space-y-3 w-full min-w-0">
                    <h3 className="text-sm font-medium text-text">Par kit</h3>
                    {poDetail.composition_snapshot.kits.map((kit: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }, idx: number) => {
                      const lineByProduct = new Map(poLines.map((l) => [l.product_id, l]));
                      return (
                        <div key={idx} className="rounded border border-border bg-surface-muted/30 p-2">
                          <div className="text-sm font-medium text-text mb-1">{kit.name}</div>
                          <div className="text-xs text-muted mb-1">Quantité assemblages : {kit.quantity}</div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left p-1">Nom / SKU</th>
                                <th className="text-right p-1">Qté/kit</th>
                                <th className="text-right p-1">Prix achat</th>
                                <th className="text-right p-1">Prix détail</th>
                                <th className="text-right p-1">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kit.lines.map((kl: { product_id: string; qty_per_kit: number }, i: number) => {
                                const line = lineByProduct.get(kl.product_id);
                                const total = kl.qty_per_kit * kit.quantity;
                                const currency = line?.currency ?? "CAD";
                                return (
                                  <tr key={i} className="border-b border-border last:border-0">
                                    <td className="p-1">{line ? `${line.description ?? line.supplier_sku} · ${line.supplier_sku}` : kl.product_id.slice(0, 8)}</td>
                                    <td className="p-1 text-right">{kl.qty_per_kit}</td>
                                    <td className="p-1 text-right">{formatOrderPrice(line?.unit_cost, currency)}</td>
                                    <td className="p-1 text-right">{formatOrderPrice(sellingByProduct[kl.product_id], currency)}</td>
                                    <td className="p-1 text-right">{total}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                <div className="w-full min-w-0">
                  <h3 className="text-sm font-medium text-text mb-2">Lignes consolidées</h3>
                  {(expandedDisplayLines ?? poLines).length === 0 ? (
                    <p className="text-sm text-muted">Aucune ligne.</p>
                  ) : (
                    <div className="rounded border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-surface-muted border-b border-border">
                            <th className="text-left p-2 font-medium">Nom / SKU</th>
                            <th className="text-right p-2 font-medium">Qté</th>
                            <th className="text-right p-2 font-medium">Reçu</th>
                            <th className="text-right p-2 font-medium">Prix achat</th>
                            <th className="text-right p-2 font-medium">Prix détail</th>
                            <th className="text-right p-2 font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(expandedDisplayLines ?? poLines).map((line, idx) => {
                            const qty = "qty" in line ? (line as { qty: number }).qty : line.qty_ordered;
                            const qtyReceived = "qty_received" in line ? (line.qty_received ?? 0) : line.qty_received;
                            const total = qty * (line.unit_cost ?? 0);
                            const isBundle = !!(line as { is_bundle?: boolean }).is_bundle;
                            const isComponent = !!(line as { parent_bundle_id?: string | null }).parent_bundle_id;
                            const productId = (line as { product_id?: string }).product_id;
                            const rowClass = [
                              "border-b border-border last:border-0",
                              isBundle && "font-bold",
                              isComponent && "italic text-muted",
                            ].filter(Boolean).join(" ");
                            const key = "id" in line ? line.id : `exp-${(line as { product_id: string }).product_id}-${(line as { parent_bundle_id?: string }).parent_bundle_id ?? ""}-${idx}`;
                            return (
                              <tr key={key} className={rowClass}>
                                <td className="p-2">{(line.description ?? (line as { supplier_sku?: string }).supplier_sku) ?? "—"} · {(line as { supplier_sku: string }).supplier_sku}</td>
                                <td className="p-2 text-right">{qty}</td>
                                <td className="p-2 text-right">{qtyReceived}</td>
                                <td className="p-2 text-right">{formatOrderPrice(line.unit_cost, line.currency)}</td>
                                <td className="p-2 text-right">{formatOrderPrice(productId ? sellingByProduct[productId] : undefined, line.currency)}</td>
                                <td className="p-2 text-right">{total.toFixed(2)} {line.currency}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted">Impossible de charger le détail.</p>
            )}
          </div>
        </div>
      )}

      {panelOrderId && poDetail && (
        <EditOrderModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          poId={panelOrderId}
          po={poDetail}
          lines={poLines}
          onSaved={() => {
            setEditModalOpen(false);
            loadDetail(panelOrderId);
            refreshOrders();
          }}
        />
      )}

      {panelOrderId && (
        <ReceiveModal
          open={receiveModalOpen}
          onClose={() => setReceiveModalOpen(false)}
          poId={panelOrderId}
          lines={poLines}
          receiveQtyByLineId={receiveQtyByLineId}
          setReceiveQtyByLineId={setReceiveQtyByLineId}
          submitting={receiveSubmitting}
          setSubmitting={setReceiveSubmitting}
          onSuccess={() => {
            setReceiveModalOpen(false);
            loadDetail(panelOrderId);
            refreshOrders();
          }}
        />
      )}

      <CreateOrderModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          setCreateModalOpen(false);
          refreshOrders();
        }}
      />
    </div>
  );
}

function ReceiveModal({
  open,
  onClose,
  poId,
  lines,
  receiveQtyByLineId,
  setReceiveQtyByLineId,
  submitting,
  setSubmitting,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  poId: string;
  lines: PurchaseOrderLineOut[];
  receiveQtyByLineId: Record<string, number>;
  setReceiveQtyByLineId: (v: Record<string, number>) => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const handleSubmit = async () => {
    const payload = {
      lines: lines
        .filter((line) => (receiveQtyByLineId[line.id] ?? 0) > 0)
        .map((line) => ({ line_id: line.id, qty_received: receiveQtyByLineId[line.id] ?? 0 })),
    };
    if (payload.lines.length === 0) {
      return;
    }
    setSubmitting(true);
    try {
      await receivePurchaseOrder(poId, payload);
      onSuccess();
    } catch (e) {
      alert((e as Error).message || "Erreur lors de la réception");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Réceptionner" open={open} onClose={onClose} showDefaultButtons={false} showCloseButton size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Saisissez la quantité reçue (delta) pour chaque ligne, puis validez.
        </p>
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-muted border-b border-border">
                <th className="text-left p-2 font-medium">Article / SKU</th>
                <th className="text-right p-2 font-medium">Commandé</th>
                <th className="text-right p-2 font-medium">Déjà reçu</th>
                <th className="text-right p-2 font-medium w-28">Qté reçue (delta)</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const remaining = Math.max(0, line.qty_ordered - line.qty_received);
                return (
                  <tr key={line.id} className="border-b border-border last:border-0">
                    <td className="p-2">{line.supplier_sku} {line.description && `— ${line.description}`}</td>
                    <td className="p-2 text-right">{line.qty_ordered}</td>
                    <td className="p-2 text-right">{line.qty_received}</td>
                    <td className="p-2 text-right">
                      <input
                        type="number"
                        min={0}
                        max={remaining}
                        step={1}
                        className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                        value={receiveQtyByLineId[line.id] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                          setReceiveQtyByLineId({ ...receiveQtyByLineId, [line.id]: isNaN(v) ? 0 : v });
                        }}
                        placeholder="0"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={submitting} />
          <Button
            label={submitting ? "Envoi…" : "Valider la réception"}
            intent="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || lines.every((l) => (receiveQtyByLineId[l.id] ?? 0) <= 0)}
          />
        </div>
      </div>
    </Modal>
  );
}

type ManualLine = {
  product_id: string;
  supplier_sku: string;
  description: string | null;
  qty_ordered: number;
  unit_cost: number;
  currency: string;
  sources?: string[];
};

type KitLine = { product_id: string; qty_per_kit: number };
type KitState = { id: string; name: string; quantity: number; lines: KitLine[] };

/** Build abbreviation from supplier name: first letters of words (e.g. "Green Aero Tech" → "GAT"), or first 3 chars if one word. */
function getSupplierAbbreviation(name: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "PO";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words
      .map((w) => (w[0] ?? "").toUpperCase())
      .join("")
      .slice(0, 6);
  }
  return (trimmed.slice(0, 3).toUpperCase() || "PO").padEnd(3, trimmed[0] ?? "X");
}

/** Suggest PO reference: ABBREV-YYYYMMDD-NNN (e.g. GAT-20250312-001). */
function suggestPoReference(abbreviation: string, orderCount: number): string {
  const today = new Date();
  const yyyymmdd =
    String(today.getFullYear()) +
    String(today.getMonth() + 1).padStart(2, "0") +
    String(today.getDate()).padStart(2, "0");
  const seq = String(orderCount + 1).padStart(3, "0");
  return `${abbreviation}-${yyyymmdd}-${seq}`;
}

/** Small product thumbnail: image from catalog or Package icon; handles load error. */
function ProductThumb({
  imagePath,
  alt = "",
  className = "w-10 h-10",
}: {
  imagePath: string | null | undefined;
  alt?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showImg = imagePath && !failed;
  return (
    <div
      className={cn("rounded border border-border bg-surface-muted flex-shrink-0 overflow-hidden flex items-center justify-center", className)}
    >
      {showImg ? (
        <img
          src={getCatalogImageUrl(imagePath)}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Package className="w-5 h-5 text-muted" />
      )}
    </div>
  );
}

/**
 * Batch add to kit: paste a list of SKUs (one per line, optional quantity).
 * Format: "SKU" or "SKU 2" or "SKU, 2" → adds product with qty 2.
 */
function BatchSkuAdd({
  products,
  onAdd,
  disabled,
  placeholder = "Un SKU par ligne. Optionnel : SKU puis quantité (ex. BC.AG.SS001 2)",
}: {
  products: SupplierProduct[];
  onAdd: (sp: SupplierProduct, qty: number) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState("");
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const handleApply = async () => {
    setMessage(null);
    const lines = text
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      setMessage({ type: "err", text: "Entrez au moins un SKU." });
      return;
    }
    const unmatched: string[] = [];
    let added = 0;
    for (const line of lines) {
      const parts = line.split(/[\s,]+/).filter(Boolean);
      const skuRaw = parts[0] ?? "";
      const qty = parts[1] != null ? Math.max(1, parseInt(parts[1], 10) || 1) : 1;
      const sku = skuRaw.trim();
      if (!sku) continue;
      const sp = products.find((p) => (p.supplier_sku ?? "").trim().toLowerCase() === sku.toLowerCase());
      if (sp) {
        await onAdd(sp, qty);
        added++;
      } else {
        unmatched.push(sku);
      }
    }
    setText("");
    if (unmatched.length === 0) {
      setMessage({ type: "ok", text: `${added} article(s) ajouté(s).` });
    } else {
      setMessage({
        type: "err",
        text: `Ajoutés : ${added}. Non trouvés : ${unmatched.join(", ")}`,
      });
    }
    if (added > 0 && unmatched.length === 0) setExpanded(false);
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        className="text-sm text-primary hover:underline"
        onClick={() => setExpanded((e) => !e)}
        disabled={disabled}
      >
        {expanded ? "Masquer l’ajout par liste" : "Ajouter par liste de SKU"}
      </button>
      {expanded && (
        <div className="flex flex-col gap-2">
          <textarea
            className="min-h-[80px] w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text font-mono"
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={disabled}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button label="Ajouter au kit" intent="secondary" size="sm" onClick={handleApply} disabled={disabled} />
            {message && (
              <span className={cn("text-sm", message.type === "ok" ? "text-green-600" : "text-danger")}>
                {message.text}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Searchable product picker with optional thumbnails; shows name, SKU, unit price (and optional qty for total). */
function ProductPicker({
  products,
  productIdToImage,
  onSelect,
  showQty = false,
  placeholder = "Rechercher un produit (nom, SKU)…",
  disabled,
}: {
  products: SupplierProduct[];
  productIdToImage: Record<string, string>;
  onSelect: (sp: SupplierProduct, qty?: number) => void;
  showQty?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [kindFilter, setKindFilter] = useState<"all" | "bundle" | "part">("all");
  const filtered = useMemo(() => {
    let list = products.filter((p) => p.is_active !== false);
    if (kindFilter === "bundle") {
      list = list.filter((p) => p.product_type === "bundle");
    } else if (kindFilter === "part") {
      list = list.filter((p) => p.product_type !== "bundle");
    }
    list = [...list].sort((a, b) => {
      const aBundle = a.product_type === "bundle" ? 0 : 1;
      const bBundle = b.product_type === "bundle" ? 0 : 1;
      if (aBundle !== bBundle) return aBundle - bBundle;
      const an = (a.product_name || a.supplier_sku || "").toLowerCase();
      const bn = (b.product_name || b.supplier_sku || "").toLowerCase();
      return an.localeCompare(bn, "fr");
    });
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (p) =>
        (p.product_name ?? "").toLowerCase().includes(q) ||
        (p.supplier_product_name ?? "").toLowerCase().includes(q) ||
        (p.supplier_sku ?? "").toLowerCase().includes(q) ||
        (p.product_code ?? "").toLowerCase().includes(q) ||
        p.product_id.toLowerCase().includes(q)
    );
  }, [products, search, kindFilter]);
  const bundleCount = useMemo(
    () => products.filter((p) => p.is_active !== false && p.product_type === "bundle").length,
    [products]
  );

  return (
    <div className="relative">
      <div className="flex gap-2 flex-wrap items-center mb-2">
        {(["all", "bundle", "part"] as const).map((k) => (
          <button
            key={k}
            type="button"
            disabled={disabled}
            onClick={() => setKindFilter(k)}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs border transition-colors",
              kindFilter === k
                ? "border-primary bg-primary/10 text-text font-medium"
                : "border-border text-muted hover:bg-surface-muted"
            )}
          >
            {k === "all" ? "Tous" : k === "bundle" ? `Bundles (${bundleCount})` : "Pièces"}
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-border bg-surface text-sm text-text placeholder:text-muted"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            disabled={disabled}
          />
        </div>
        {showQty && (
          <div className="flex items-center gap-1">
            <span className="text-sm text-muted">Qté/kit</span>
            <input
              type="number"
              min={1}
              step={1}
              className="w-16 h-9 rounded border border-border bg-surface px-2 text-sm text-right"
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          </div>
        )}
      </div>
      {open && filtered.length === 0 && (
        <p className="mt-1 text-xs text-muted">
          {products.length === 0
            ? "Aucun produit actif pour ce fournisseur. Liez des pièces dans Catalogue → Approvisionnement, ou choisissez un bundle catalogue (mode Par kits)."
            : "Aucun résultat pour ce filtre ou cette recherche."}
        </p>
      )}
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border bg-surface shadow-lg py-1"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.slice(0, 50).map((sp) => {
            const img = productIdToImage[sp.product_id];
            const name = (sp.product_name || sp.supplier_product_name || sp.supplier_sku || "—").trim() || "—";
            return (
              <li
                key={sp.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-surface-muted cursor-pointer text-left"
                onClick={() => {
                  onSelect(sp, showQty ? qty : undefined);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <ProductThumb imagePath={img} className="w-10 h-10" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-medium text-text truncate">{name}</div>
                    {sp.product_type === "bundle" && (
                      <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">
                        Bundle
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted">
                    {sp.supplier_sku}
                    {sp.product_code && sp.product_code !== sp.supplier_sku ? ` · ${sp.product_code}` : ""}
                    {" · "}
                    {sp.product_type === "bundle" && sp.unit_cost === 0
                      ? "Prix via composants"
                      : `${sp.unit_cost} ${sp.currency}`}
                    {showQty &&
                      sp.unit_cost > 0 &&
                      ` · Total: ${(sp.unit_cost * qty).toFixed(2)} ${sp.currency}`}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CreateOrderModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>([]);
  const [supplierId, setSupplierId] = useState<string>("");
  const [orderDate, setOrderDate] = useState<string>("");
  const [expectedDate, setExpectedDate] = useState<string>("");
  const [status, setStatus] = useState<string>("draft");
  const [notes, setNotes] = useState<string>("");
  const [supplierReference, setSupplierReference] = useState<string>("");
  const [orderMode, setOrderMode] = useState<"manual" | "kits">("manual");
  const [kits, setKits] = useState<KitState[]>([]);
  const [lines, setLines] = useState<ManualLine[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [kitExpanding, setKitExpanding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productIdToImage, setProductIdToImage] = useState<Record<string, string>>({});
  const [sellingByProduct, setSellingByProduct] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open) return;
    setLoadingSuppliers(true);
    setSupplierReference("");
    listSuppliers({ page_size: 500, status: "active" })
      .then((list) => {
        setSuppliers(list);
        setSupplierId("");
        setSupplierProducts([]);
        setLines([]);
        setKits([]);
      })
      .catch(() => setSuppliers([]))
      .finally(() => setLoadingSuppliers(false));
  }, [open]);

  // Suggest PO reference when supplier is selected: ABBREV-YYYYMMDD-NNN (e.g. GAT-20250312-001)
  useEffect(() => {
    if (!open || !supplierId || suppliers.length === 0) return;
    const supplier = suppliers.find((s) => s.id === supplierId);
    const abbrev = supplier ? getSupplierAbbreviation(supplier.name) : "PO";
    listPurchaseOrders({ supplier_id: supplierId, page_size: 500 })
      .then((orders) => {
        setSupplierReference(suggestPoReference(abbrev, orders.length));
      })
      .catch(() => {
        setSupplierReference(suggestPoReference(abbrev, 0));
      });
  }, [open, supplierId, suppliers]);

  useEffect(() => {
    if (!open || !supplierId) {
      setSupplierProducts([]);
      setProductIdToImage({});
      if (orderMode === "manual") setLines([]);
      return;
    }
    setLoadingProducts(true);
    listSupplierProducts(supplierId, { includeCatalogBundles: orderMode === "kits" })
      .then((sps) => setSupplierProducts(sps.filter((sp) => sp.is_active !== false)))
      .catch(() => setSupplierProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [open, supplierId, orderMode]);

  // Images catalogue pour tous les produits du fournisseur (picker commande)
  useEffect(() => {
    if (!open || !supplierId || supplierProducts.length === 0) return;
    const uniqueIds = [...new Set(supplierProducts.map((sp) => sp.product_id).filter(Boolean))];
    let cancelled = false;
    runInBatches(uniqueIds, PRODUCT_IMAGE_BATCH_SIZE, (id) =>
      getProduct(id)
        .then((p) => (p.image_path ? { id: p.id, image_path: p.image_path } : null))
        .catch(() => null)
    ).then((results) => {
      if (cancelled) return;
      setProductIdToImage((prev) => {
        const map = { ...prev };
        results.forEach((r) => {
          if (r?.image_path) map[r.id] = r.image_path;
        });
        return map;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, supplierId, supplierProducts]);

  // Load images only for products already in lines or kits (batch to avoid ERR_INSUFFICIENT_RESOURCES)
  useEffect(() => {
    if (!open || !supplierId) return;
    const ids = new Set<string>();
    lines.forEach((l) => l.product_id && ids.add(l.product_id));
    kits.forEach((k) => k.lines.forEach((ll) => ll.product_id && ids.add(ll.product_id)));
    const uniqueIds = [...ids];
    if (uniqueIds.length === 0) {
      setProductIdToImage({});
      return;
    }
    let cancelled = false;
    runInBatches(uniqueIds, PRODUCT_IMAGE_BATCH_SIZE, (id) =>
      getProduct(id)
        .then((p) => (p.image_path ? { id: p.id, image_path: p.image_path } : null))
        .catch(() => null)
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      results.forEach((r) => {
        if (r?.image_path) map[r.id] = r.image_path;
      });
      setProductIdToImage(map);
    });
    return () => {
      cancelled = true;
    };
  }, [open, supplierId, lines, kits]);

  useEffect(() => {
    if (!open) return;
    const ids = new Set<string>();
    lines.forEach((l) => l.product_id && ids.add(l.product_id));
    kits.forEach((k) => k.lines.forEach((ll) => ll.product_id && ids.add(ll.product_id)));
    const uniqueIds = [...ids];
    if (uniqueIds.length === 0) {
      setSellingByProduct({});
      return;
    }
    let cancelled = false;
    fetchSellingAndNamesByProduct(uniqueIds).then(({ sellingByProduct: prices }) => {
      if (!cancelled) setSellingByProduct(prices);
    });
    return () => {
      cancelled = true;
    };
  }, [open, lines, kits]);

  // When kits change, call compute-lines and set consolidated lines
  useEffect(() => {
    if (!open || orderMode !== "kits" || !supplierId || kits.length === 0) return;
    const hasAnyLine = kits.some((k) => k.lines.length > 0);
    if (!hasAnyLine) {
      setLines([]);
      return;
    }
    setConsolidatedLoading(true);
    setError(null);
    computePurchaseOrderLines({
      supplier_id: supplierId,
      kits: kits.map((k) => ({
        name: k.name,
        quantity: k.quantity,
        lines: k.lines.map((l) => ({ product_id: l.product_id, qty_per_kit: l.qty_per_kit })),
      })),
    })
      .then((res) => {
        setLines(
          res.consolidated_lines.map((l: ConsolidatedLineOut) => ({
            product_id: l.product_id,
            supplier_sku: l.supplier_sku,
            description: l.description,
            qty_ordered: l.qty,
            unit_cost: l.unit_cost,
            currency: l.currency,
            sources: l.sources ?? [],
            is_bundle: l.is_bundle ?? false,
            parent_bundle_id: l.parent_bundle_id ?? undefined,
          }))
        );
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Erreur calcul des lignes"))
      .finally(() => setConsolidatedLoading(false));
  }, [open, orderMode, supplierId, kits]);

  const addLine = (sp: SupplierProduct) => {
    setLines((prev) => [
      ...prev,
      {
        product_id: sp.product_id,
        supplier_sku: sp.supplier_sku,
        description: sp.supplier_product_name ?? null,
        qty_ordered: 1,
        unit_cost: sp.unit_cost,
        currency: sp.currency ?? "CAD",
      },
    ]);
  };

  const updateLineQty = (index: number, qty: number) => {
    setLines((prev) => {
      const next = [...prev];
      const intQty = Math.max(0, Math.floor(Number(qty)) || 0);
      if (next[index]) next[index] = { ...next[index], qty_ordered: intQty };
      return next;
    });
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const addKit = () => {
    setKits((prev) => [{ id: crypto.randomUUID(), name: `Kit ${prev.length + 1}`, quantity: 1, lines: [] }, ...prev]);
  };
  const updateKit = (kitId: string, patch: Partial<KitState>) => {
    setKits((prev) => prev.map((k) => (k.id === kitId ? { ...k, ...patch } : k)));
  };
  const removeKit = (kitId: string) => {
    setKits((prev) => prev.filter((k) => k.id !== kitId));
  };
  const addKitLine = async (kitId: string, sp: SupplierProduct, qtyPerKit: number) => {
    setKitExpanding(true);
    setError(null);
    try {
      const intQty = Math.max(1, Math.floor(Number(qtyPerKit)) || 1);
      const expanded = await expandProductToKitLines(sp.product_id, intQty);
      setKits((prev) =>
        prev.map((k) => (k.id === kitId ? { ...k, lines: mergeKitLines(k.lines, expanded) } : k))
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'ajout au kit");
    } finally {
      setKitExpanding(false);
    }
  };
  const removeKitLine = (kitId: string, lineIndex: number) => {
    setKits((prev) =>
      prev.map((k) => (k.id === kitId ? { ...k, lines: k.lines.filter((_, i) => i !== lineIndex) } : k))
    );
  };
  const updateKitLineQty = (kitId: string, lineIndex: number, qty_per_kit: number) => {
    const intQty = Math.max(1, Math.floor(Number(qty_per_kit)) || 1);
    setKits((prev) =>
      prev.map((k) => {
        if (k.id !== kitId) return k;
        const next = [...k.lines];
        if (next[lineIndex]) next[lineIndex] = { ...next[lineIndex], qty_per_kit: intQty };
        return { ...k, lines: next };
      })
    );
  };

  const [exporting, setExporting] = useState(false);
  const exportExcel = useCallback(async () => {
    if (lines.length === 0) return;
    setExporting(true);
    try {
      const supplierName = suppliers.find((s) => s.id === supplierId)?.name ?? "Fournisseur";
      const uniqueIds = [...new Set(lines.map((l) => l.product_id))];
      const { sellingByProduct, nameByProduct } = await fetchSellingAndNamesByProduct(uniqueIds);
      const nameBySku = buildNameBySkuFromSupplierProducts(supplierProducts);
      const wb = XLSX.utils.book_new();
      const safeName = (s: string) => (s.length > 31 ? s.slice(0, 31) : s).replace(/[*?:/\\[\]]/g, " ");
      const productByProductId = new Map(lines.map((l) => [l.product_id, l]));
      const bundleSkuByProductId = new Map<string, string>();
      lines.forEach((l) => {
        if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
      });
      const partOrBundleBySku = buildPartOrBundleBySku(lines.filter((l) => !l.is_bundle), bundleSkuByProductId);
      const table1Lines = groupExportLinesBySku(lines.filter((l) => !l.is_bundle), "qty_ordered");
      const cmdDataStartRow = 8;
      const cmdSheetRows: (string | number)[][] = [
        ["Fournisseur", supplierName],
        ["Date", orderDate || ""],
        ["Référence", supplierReference || ""],
        [],
        [EXCEL_LIST_TITLE],
        [],
        ["Nom", "SKU", EXCEL_ITEM_NAME_COL, EXCEL_PART_BUNDLE_COL, "Qté", "Coût unit.", "Prix détail", "Prix total", "Devise"],
        ...table1Lines.map((l) => {
          const prixDetail = sellingByProduct[l.product_id];
          const partOrBundle = partOrBundleBySku.get((l.supplier_sku ?? "").trim()) ?? "Part";
          return [
            excelSupplierLineName(l, nameBySku),
            l.supplier_sku,
            excelCatalogLineName(l.product_id, nameByProduct, l, nameBySku),
            partOrBundle,
            l.qty_ordered,
            l.unit_cost ?? "",
            typeof prixDetail === "number" && !Number.isNaN(prixDetail) ? prixDetail : "",
            0,
            l.currency,
          ];
        }),
        ["TOTAL", "", "", "", 0, "", "", 0, ""],
        ...excelBundleReferenceRows(lines, "qty_ordered", nameByProduct, nameBySku),
      ];
      const wsCmd = XLSX.utils.aoa_to_sheet(cmdSheetRows);
      applyCurrencyFormat(wsCmd, [5, 6, 7], cmdDataStartRow);
      applyExcelFormulas(wsCmd, cmdDataStartRow, table1Lines.length, 4, 5, 7, [4, 7]);
      XLSX.utils.book_append_sheet(wb, wsCmd, safeName("COMMANDE"));
      for (const kit of kits) {
        const computed = await computePurchaseOrderLines({
          supplier_id: supplierId!,
          kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
        });
        const kitLinesExport = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
          const base = productByProductId.get(l.product_id);
          return {
            product_id: l.product_id,
            supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
            description: l.description ?? base?.description ?? null,
            qty: l.qty,
            unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
            currency: l.currency ?? base?.currency ?? "CAD",
            is_bundle: l.is_bundle,
            parent_bundle_id: l.parent_bundle_id ?? undefined,
          };
        });
        const kitBundleSkuByProductId = new Map<string, string>();
        kitLinesExport.forEach((l: { is_bundle?: boolean; product_id: string; supplier_sku: string }) => {
          if (l.is_bundle) kitBundleSkuByProductId.set(l.product_id, l.supplier_sku);
        });
        const kitPartOrBundleBySku = buildPartOrBundleBySku(
          kitLinesExport.filter((l: { is_bundle?: boolean }) => !l.is_bundle),
          kitBundleSkuByProductId
        );
        const k1 = groupExportLinesBySku(kitLinesExport.filter((l: { is_bundle?: boolean }) => !l.is_bundle), "qty");
        const kitDataStartRow = 6;
        const kitSheetRows: (string | number)[][] = [
          ["Kit", kit.name],
          ["Quantité assemblages", kit.quantity],
          [],
          [EXCEL_LIST_TITLE],
          [],
          ["Nom", "SKU", EXCEL_ITEM_NAME_COL, EXCEL_PART_BUNDLE_COL, "Qté/kit ou Total", "Coût unit.", "Prix total", "Devise"],
          ...k1.map((l: { description: string | null; supplier_sku: string; product_id?: string; qty: number; unit_cost: number; currency: string }) => {
            const partOrBundle = kitPartOrBundleBySku.get((l.supplier_sku ?? "").trim()) ?? "Part";
            return [
              excelSupplierLineName(l, nameBySku),
              l.supplier_sku,
              excelCatalogLineName(l.product_id, nameByProduct, l, nameBySku),
              partOrBundle,
              l.qty,
              l.unit_cost ?? "",
              0,
              l.currency,
            ];
          }),
          ["TOTAL", "", "", "", 0, "", 0, ""],
          ...excelBundleReferenceRows(kitLinesExport, "qty", nameByProduct, nameBySku),
        ];
        const wsKit = XLSX.utils.aoa_to_sheet(kitSheetRows);
        applyCurrencyFormat(wsKit, [5, 6], kitDataStartRow);
        applyExcelFormulas(wsKit, kitDataStartRow, k1.length, 4, 5, 6, [4, 6]);
        XLSX.utils.book_append_sheet(wb, wsKit, safeName(kit.name || "Kit"));
      }
      const filename = `commande_${supplierName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } finally {
      setExporting(false);
    }
  }, [supplierId, suppliers, orderDate, supplierReference, lines, kits, supplierProducts]);

  const handleExportPdf = useCallback(async () => {
    if (lines.length === 0) return;
    const supplierName = suppliers.find((s) => s.id === supplierId)?.name ?? "Fournisseur";
    const orderLinesOnly = lines.filter((l) => !l.parent_bundle_id);
    const productByProductId = new Map(lines.map((l) => [l.product_id, l]));
    const bundleSkuByProductId = new Map<string, string>();
    lines.forEach((l) => {
      if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
    });
    const pdfLines: PdfLine[] = lines.map((l) => ({
      description: l.description,
      supplier_sku: l.supplier_sku,
      qty_ordered: l.qty_ordered,
      unit_cost: l.unit_cost,
      currency: l.currency ?? "CAD",
      is_bundle: l.is_bundle,
      parent_bundle_sku: l.parent_bundle_id ? (bundleSkuByProductId.get(l.parent_bundle_id) ?? null) : null,
    }));
    const pdfKits: PdfKit[] = kits.length
      ? await Promise.all(
          kits.map(async (kit) => {
            const computed = await computePurchaseOrderLines({
              supplier_id: supplierId!,
              kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
            });
            const kitBundleSku = new Map<string, string>();
            computed.consolidated_lines.forEach((l: { is_bundle?: boolean; product_id: string; supplier_sku: string }) => {
              if (l.is_bundle) kitBundleSku.set(l.product_id, l.supplier_sku);
            });
            const kitLines: PdfLine[] = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
              const base = productByProductId.get(l.product_id);
              return {
                description: l.description ?? base?.description ?? null,
                supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
                qty_ordered: l.qty,
                unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
                currency: l.currency ?? base?.currency ?? "CAD",
                qty_for_this_kit: l.qty,
                is_bundle: l.is_bundle,
                parent_bundle_sku: l.parent_bundle_id ? (kitBundleSku.get(l.parent_bundle_id) ?? null) : null,
              };
            });
            return { name: kit.name, quantity: kit.quantity, lines: kitLines };
          })
        )
      : [];
    exportOrderPdf({
      supplierName,
      supplierReference: supplierReference || undefined,
      orderDate: orderDate || undefined,
      expectedDate: expectedDate || undefined,
      status,
      notes: notes || undefined,
      lines: pdfLines,
      kits: pdfKits.length ? pdfKits : undefined,
      filename: `commande_${supplierName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`,
    });
  }, [supplierId, suppliers, supplierReference, orderDate, expectedDate, status, notes, lines, kits]);

  const handleExportPdfWithImages = useCallback(async () => {
    if (lines.length === 0) return;
    setExporting(true);
    try {
      const supplierName = suppliers.find((s) => s.id === supplierId)?.name ?? "Fournisseur";
      const productByProductId = new Map(lines.map((l) => [l.product_id, l]));
      const bundleSkuByProductId = new Map<string, string>();
      lines.forEach((l) => {
        if (l.is_bundle) bundleSkuByProductId.set(l.product_id, l.supplier_sku);
      });
      const uniqueProductIds = [...new Set(lines.map((l) => l.product_id).filter(Boolean))] as string[];
      const imageMap = new Map<string, string | null>();
      for (const productId of uniqueProductIds) {
        try {
          const product = await getProduct(productId);
          const path = (product as { image_path?: string | null }).image_path;
          if (path) {
            const dataUrl = await imageUrlToDataUrl(getCatalogImageUrl(path));
            imageMap.set(productId, dataUrl);
          } else {
            imageMap.set(productId, null);
          }
        } catch {
          imageMap.set(productId, null);
        }
      }
      const pdfLines: PdfLine[] = lines.map((l) => ({
        description: l.description,
        supplier_sku: l.supplier_sku,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency ?? "CAD",
        is_bundle: l.is_bundle,
        parent_bundle_sku: l.parent_bundle_id ? (bundleSkuByProductId.get(l.parent_bundle_id) ?? null) : null,
        imageDataUrl: l.product_id ? (imageMap.get(l.product_id) ?? null) : null,
      }));
      const pdfKits: PdfKit[] = kits.length
        ? await Promise.all(
            kits.map(async (kit) => {
              const computed = await computePurchaseOrderLines({
                supplier_id: supplierId!,
                kits: [{ name: kit.name, quantity: kit.quantity, lines: kit.lines.map((ll) => ({ product_id: ll.product_id, qty_per_kit: ll.qty_per_kit })) }],
              });
              const kitBundleSku = new Map<string, string>();
              computed.consolidated_lines.forEach((l: { is_bundle?: boolean; product_id: string; supplier_sku: string }) => {
                if (l.is_bundle) kitBundleSku.set(l.product_id, l.supplier_sku);
              });
              const kitLines: PdfLine[] = computed.consolidated_lines.map((l: ConsolidatedLineOut) => {
                const base = productByProductId.get(l.product_id);
                return {
                  description: l.description ?? base?.description ?? null,
                  supplier_sku: l.supplier_sku ?? base?.supplier_sku ?? "",
                  qty_ordered: l.qty,
                  unit_cost: l.unit_cost ?? base?.unit_cost ?? 0,
                  currency: l.currency ?? base?.currency ?? "CAD",
                  qty_for_this_kit: l.qty,
                  is_bundle: l.is_bundle,
                  parent_bundle_sku: l.parent_bundle_id ? (kitBundleSku.get(l.parent_bundle_id) ?? null) : null,
                  imageDataUrl: imageMap.get(l.product_id) ?? null,
                };
              });
              return { name: kit.name, quantity: kit.quantity, lines: kitLines };
            })
          )
        : [];
      const baseName = `commande_${supplierName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}`;
      exportOrderPdf({
        supplierName,
        supplierReference: supplierReference || undefined,
        orderDate: orderDate || undefined,
        expectedDate: expectedDate || undefined,
        status,
        notes: notes || undefined,
        lines: pdfLines,
        kits: pdfKits.length ? pdfKits : undefined,
        withImages: true,
        filename: `${baseName}_avec_images.pdf`,
      });
    } finally {
      setExporting(false);
    }
  }, [supplierId, suppliers, supplierReference, orderDate, expectedDate, status, notes, lines, kits]);

  const handleSubmit = async () => {
    if (!supplierId) {
      setError("Veuillez sélectionner un fournisseur.");
      return;
    }
    if (lines.length === 0) {
      setError("Ajoutez au moins une ligne (ou construisez des kits).");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const composition_snapshot =
        orderMode === "kits" && kits.length > 0
          ? {
              kits: kits.map((k) => ({
                name: k.name,
                quantity: k.quantity,
                lines: k.lines.map((l) => ({ product_id: l.product_id, qty_per_kit: l.qty_per_kit })),
              })),
              generated_at: new Date().toISOString(),
            }
          : undefined;
      const po = await createPurchaseOrder({
        supplier_id: supplierId,
        status,
        supplier_reference: supplierReference || null,
        order_date: orderDate || null,
        expected_date: expectedDate || null,
        notes: notes || null,
        composition_snapshot: composition_snapshot ?? undefined,
      });
      // Toutes les lignes consolidées sont commandables (bundles explosés en pièces)
      for (const line of lines) {
        await createPurchaseOrderLine(po.id, {
          product_id: line.product_id,
          supplier_sku: line.supplier_sku,
          description: line.description,
          qty_ordered: line.qty_ordered,
          qty_received: 0,
          unit_cost: line.unit_cost,
          currency: line.currency,
        });
      }
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Créer une commande" open={open} onClose={onClose} showDefaultButtons={false} showCloseButton size="4xl">
      <div className="space-y-5 min-h-[70vh]">
        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3 text-sm text-danger">
            {error}
          </div>
        )}
        <section>
          <h3 className="text-sm font-semibold text-text mb-2">Informations générales</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Fournisseur</label>
              <select
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                disabled={loadingSuppliers}
              >
                <option value="">— Sélectionner —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Référence / N° PO"
              value={supplierReference}
              onChange={(e) => setSupplierReference(e.target.value)}
              placeholder="Optionnel"
            />
            <Input label="Date commande" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
            <Input label="Date prévue" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-text mb-1">Statut</label>
              <select
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="draft">Brouillon</option>
                <option value="sent">Envoyée</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Notes</label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                placeholder="Notes optionnelles"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </section>
        <section>
          <h3 className="text-sm font-semibold text-text mb-2">Construire la commande</h3>
          {supplierId && (
            <>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="orderMode" checked={orderMode === "manual"} onChange={() => setOrderMode("manual")} />
                  Saisie manuelle
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="orderMode" checked={orderMode === "kits"} onChange={() => setOrderMode("kits")} />
                  Par kits
                </label>
              </div>
              {orderMode === "kits" && (
                <div className="mb-4 rounded border border-border p-3 bg-surface-muted/30">
                  <p className="text-xs text-muted mb-3">
                    Un bundle ajouté au kit se décompose immédiatement en pièces (quantités modifiables, retrait possible). Ajoutez d&apos;autres articles séparément si besoin.
                  </p>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-text">Kits à assembler</h4>
                    <Button label="+ Ajouter un kit" intent="default" size="sm" onClick={addKit} />
                  </div>
                  {kits.length === 0 ? (
                    <p className="text-sm text-muted">Aucun kit. Cliquez sur « Ajouter un kit ».</p>
                  ) : (
                    <div className="space-y-3">
                      {kits.map((kit) => (
                        <div key={kit.id} className="rounded border border-border bg-surface p-3">
                          <div className="flex flex-wrap gap-2 items-center mb-2">
                            <input
                              className="w-40 rounded border border-border bg-surface px-2 py-1 text-sm"
                              value={kit.name}
                              onChange={(e) => updateKit(kit.id, { name: e.target.value })}
                              placeholder="Nom du kit"
                            />
                            <span className="text-sm text-muted">Qté</span>
                            <input
                              type="number"
                              min={1}
                              className="w-16 rounded border border-border bg-surface px-2 py-1 text-sm"
                              value={kit.quantity}
                              onChange={(e) => updateKit(kit.id, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            />
                            <button type="button" className="text-danger text-xs hover:underline" onClick={() => removeKit(kit.id)}>Supprimer</button>
                          </div>
                          <div className="mb-2">
                            <label className="block text-xs text-muted mb-1">Ajouter un article</label>
                            <ProductPicker
                              products={supplierProducts}
                              productIdToImage={productIdToImage}
                              showQty
                              placeholder="Rechercher (nom, SKU)…"
                              disabled={loadingProducts || kitExpanding}
                              onSelect={(sp, q) => void addKitLine(kit.id, sp, q ?? 1)}
                            />
                          </div>
                          <div className="mb-2">
                            <BatchSkuAdd
                              products={supplierProducts}
                              onAdd={(sp, qty) => addKitLine(kit.id, sp, qty)}
                              disabled={loadingProducts || kitExpanding}
                              placeholder="Un SKU par ligne. Optionnel : SKU puis quantité (ex. BC.AG.SS001 2)"
                            />
                          </div>
                          {kitExpanding && <p className="text-xs text-muted mb-2">Décomposition du bundle…</p>}
                          {kit.lines.length > 0 && (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="w-12 p-1"></th>
                                  <th className="text-left p-1">Nom / SKU</th>
                                  <th className="text-right p-1">Prix achat</th>
                                  <th className="text-right p-1">Prix détail</th>
                                  <th className="text-right p-1">Qté/kit</th>
                                  <th className="text-right p-1">Total</th>
                                  <th className="w-12"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {kit.lines.map((kl, idx) => {
                                  const sp = supplierProducts.find((p) => p.product_id === kl.product_id);
                                  const total = sp ? sp.unit_cost * kl.qty_per_kit : 0;
                                  return (
                                    <tr key={idx} className="border-b border-border last:border-0">
                                      <td className="p-1 align-middle">
                                        <ProductThumb imagePath={productIdToImage[kl.product_id]} className="w-8 h-8" />
                                      </td>
                                      <td className="p-1">{sp ? `${(sp.product_name || sp.supplier_product_name || sp.supplier_sku).trim() || "—"} · ${sp.supplier_sku}` : "—"}</td>
                                      <td className="p-1 text-right">{sp ? formatOrderPrice(sp.unit_cost, sp.currency) : "—"}</td>
                                      <td className="p-1 text-right">{formatOrderPrice(sellingByProduct[kl.product_id], sp?.currency ?? "CAD")}</td>
                                      <td className="p-1 text-right">
                                        <input
                                          type="number"
                                          min={1}
                                          step={1}
                                          className="w-14 rounded border border-border bg-surface px-1 py-0.5 text-right text-xs"
                                          value={kl.qty_per_kit}
                                          onChange={(e) => updateKitLineQty(kit.id, idx, Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        />
                                      </td>
                                      <td className="p-1 text-right">{sp ? `${total.toFixed(2)} ${sp.currency}` : "—"}</td>
                                      <td className="p-1"><button type="button" className="text-danger hover:underline" onClick={() => removeKitLine(kit.id, idx)}>×</button></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <h3 className="text-sm font-semibold text-text mb-2">Pièces à commander</h3>
              {orderMode === "manual" && (
              <div className="mb-2">
                <label className="block text-sm text-muted mb-1">Ajouter un article</label>
                <ProductPicker
                  products={supplierProducts}
                  productIdToImage={productIdToImage}
                  placeholder="Rechercher un produit (nom, SKU)…"
                  disabled={loadingProducts}
                  onSelect={(sp) => addLine(sp)}
                />
              </div>
              )}
              {orderMode === "kits" && consolidatedLoading && <p className="text-sm text-muted mb-2">Calcul des lignes…</p>}
              {lines.length === 0 && !consolidatedLoading ? (
                <div className="rounded border border-border p-4 text-center text-muted text-sm">
                  {orderMode === "manual" ? "Aucune ligne. Sélectionnez un produit ci-dessus." : "Ajoutez des kits et des produits pour voir les pièces consolidées."}
                </div>
              ) : (
                <div className="rounded border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface-muted border-b border-border">
                        <th className="w-14 p-2 font-medium"></th>
                        <th className="text-left p-2 font-medium">Nom</th>
                        <th className="text-left p-2 font-medium">SKU</th>
                        <th className="text-right p-2 font-medium">Qté</th>
                        <th className="text-right p-2 font-medium">Prix achat</th>
                        <th className="text-right p-2 font-medium">Prix détail</th>
                        <th className="text-right p-2 font-medium">Prix total</th>
                        {lines.some((l) => l.sources?.length) ? <th className="text-left p-2 font-medium">Source</th> : null}
                        <th className="text-right p-2 font-medium w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, i) => {
                        const total = line.qty_ordered * (line.unit_cost ?? 0);
                        return (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="p-2 align-middle">
                              <ProductThumb imagePath={productIdToImage[line.product_id]} className="w-9 h-9" />
                            </td>
                            <td className="p-2">{line.description ?? "—"}</td>
                            <td className="p-2">{line.supplier_sku}</td>
                            <td className="p-2 text-right">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                                value={line.qty_ordered}
                                onChange={(e) => updateLineQty(i, Math.max(0, parseInt(e.target.value, 10) || 0))}
                              />
                            </td>
                            <td className="p-2 text-right">{formatOrderPrice(line.unit_cost, line.currency)}</td>
                            <td className="p-2 text-right">{formatOrderPrice(sellingByProduct[line.product_id], line.currency)}</td>
                            <td className="p-2 text-right">{total.toFixed(2)} {line.currency}</td>
                            {lines.some((l) => l.sources?.length) ? (
                              <td className="p-2 text-muted text-xs">
                                {(line.sources ?? []).join(", ") || "—"}
                              </td>
                            ) : null}
                            <td className="p-2 text-right">
                              <button type="button" className="text-danger hover:underline text-xs" onClick={() => removeLine(i)}>Retirer</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {!supplierId && (
            <p className="text-sm text-muted">Sélectionnez d’abord un fournisseur pour ajouter des lignes ou des kits.</p>
          )}
        </section>
        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <Button
            label={exporting ? "Export…" : "Exporter Excel"}
            intent="default"
            size="sm"
            onClick={() => exportExcel()}
            disabled={lines.length === 0 || exporting}
          />
          <Button
            label={exporting ? "Export…" : "Exporter PDF"}
            intent="default"
            size="sm"
            onClick={() => handleExportPdf()}
            disabled={lines.length === 0 || exporting}
          />
          <Button
            label={exporting ? "Export…" : "Exporter PDF (avec images)"}
            intent="default"
            size="sm"
            onClick={() => handleExportPdfWithImages()}
            disabled={lines.length === 0 || exporting}
          />
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={submitting} />
          <Button
            label={submitting ? "Enregistrement…" : "Enregistrer"}
            intent="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || !supplierId || lines.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
}

type EditLineState = {
  id?: string;
  product_id: string;
  supplier_sku: string;
  description: string | null;
  qty_ordered: number;
  unit_cost: number;
  currency: string;
};

function EditOrderModal({
  open,
  onClose,
  poId,
  po,
  lines: initialLines,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  poId: string;
  po: PurchaseOrderOut;
  lines: PurchaseOrderLineOut[];
  onSaved: () => void;
}) {
  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>([]);
  const [productIdToImage, setProductIdToImage] = useState<Record<string, string>>({});
  const [supplierReference, setSupplierReference] = useState(po.supplier_reference ?? "");
  const [orderDate, setOrderDate] = useState(po.order_date ?? "");
  const [expectedDate, setExpectedDate] = useState(po.expected_date ?? "");
  const [status, setStatus] = useState(po.status);
  const [notes, setNotes] = useState(po.notes ?? "");
  const [lines, setLines] = useState<EditLineState[]>(() =>
    initialLines.map((l) => ({
      id: l.id,
      product_id: l.product_id,
      supplier_sku: l.supplier_sku,
      description: l.description,
      qty_ordered: l.qty_ordered,
      unit_cost: l.unit_cost,
      currency: l.currency,
    }))
  );
  const [kits, setKits] = useState<KitState[]>(() => {
    const snap = po.composition_snapshot?.kits;
    if (!Array.isArray(snap) || snap.length === 0) return [];
    return snap.map((k: { name?: string; quantity?: number; lines?: { product_id: string; qty_per_kit: number }[] }, idx: number) => ({
      id: crypto.randomUUID(),
      name: k.name ?? `Kit ${idx + 1}`,
      quantity: typeof k.quantity === "number" ? Math.max(1, Math.floor(k.quantity)) : 1,
      lines: Array.isArray(k.lines) ? k.lines.map((ll) => ({ product_id: ll.product_id, qty_per_kit: Math.max(1, Math.floor(Number(ll.qty_per_kit)) || 1) })) : [],
    }));
  });
  const [loading, setLoading] = useState(false);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [kitExpanding, setKitExpanding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellingByProduct, setSellingByProduct] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open || !po.supplier_id) return;
    setSupplierReference(po.supplier_reference ?? "");
    setOrderDate(po.order_date ?? "");
    setExpectedDate(po.expected_date ?? "");
    setStatus(po.status);
    setNotes(po.notes ?? "");
    setLines(
      initialLines.map((l) => ({
        id: l.id,
        product_id: l.product_id,
        supplier_sku: l.supplier_sku,
        description: l.description,
        qty_ordered: l.qty_ordered,
        unit_cost: l.unit_cost,
        currency: l.currency,
      }))
    );
    const snap = po.composition_snapshot?.kits;
    if (!Array.isArray(snap) || snap.length === 0) {
      setKits([]);
      return;
    }
    let cancelled = false;
    setKitExpanding(true);
    Promise.all(
      snap.map(async (k: { name?: string; quantity?: number; lines?: { product_id: string; qty_per_kit: number }[] }, idx: number) => ({
        id: crypto.randomUUID(),
        name: k.name ?? `Kit ${idx + 1}`,
        quantity: typeof k.quantity === "number" ? Math.max(1, Math.floor(k.quantity)) : 1,
        lines: await expandKitLinesSnapshot(
          Array.isArray(k.lines)
            ? k.lines.map((ll) => ({
                product_id: ll.product_id,
                qty_per_kit: Math.max(1, Math.floor(Number(ll.qty_per_kit)) || 1),
              }))
            : []
        ),
      }))
    )
      .then((expandedKits) => {
        if (!cancelled) setKits(expandedKits);
      })
      .catch(() => {
        if (!cancelled) setKits([]);
      })
      .finally(() => {
        if (!cancelled) setKitExpanding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, po, initialLines]);

  // Quand les kits changent, recalculer les lignes consolidées et mettre à jour le tableau "Lignes"
  useEffect(() => {
    if (!open || !po.supplier_id || kits.length === 0) return;
    const hasAnyLine = kits.some((k) => k.lines.length > 0);
    if (!hasAnyLine) {
      setLines([]);
      return;
    }
    setConsolidatedLoading(true);
    setError(null);
    computePurchaseOrderLines({
      supplier_id: po.supplier_id,
      kits: kits.map((k) => ({
        name: k.name,
        quantity: k.quantity,
        lines: k.lines.map((l) => ({ product_id: l.product_id, qty_per_kit: l.qty_per_kit })),
      })),
    })
      .then((res) => {
        setLines((prev) => {
          const consolidated = res.consolidated_lines.map((l: ConsolidatedLineOut) => {
              const existing = prev.find((pl) => pl.product_id === l.product_id) ?? initialLines.find((il) => il.product_id === l.product_id);
              return {
                id: existing?.id,
                product_id: l.product_id,
                supplier_sku: l.supplier_sku,
                description: l.description,
                qty_ordered: l.qty,
                unit_cost: l.unit_cost,
                currency: l.currency ?? "CAD",
              };
            });
          return consolidated;
        });
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Erreur calcul des lignes"))
      .finally(() => setConsolidatedLoading(false));
  }, [open, po.supplier_id, kits]);

  useEffect(() => {
    if (!open || !po.supplier_id) return;
    setLoading(true);
    listSupplierProducts(po.supplier_id, { includeCatalogBundles: true })
      .then((sps) => {
        setSupplierProducts(sps.filter((sp) => sp.is_active !== false));
        const ids = new Set<string>();
        initialLines.forEach((l) => l.product_id && ids.add(l.product_id));
        (po.composition_snapshot?.kits ?? []).forEach(
          (k: { lines?: { product_id: string }[] }) =>
            (k.lines ?? []).forEach((ll: { product_id: string }) => ll.product_id && ids.add(ll.product_id))
        );
        const uniqueIds = [...ids];
        if (uniqueIds.length === 0) {
          setProductIdToImage({});
          return;
        }
        return runInBatches(uniqueIds, PRODUCT_IMAGE_BATCH_SIZE, (id) =>
          getProduct(id)
            .then((p) => (p.image_path ? { id: p.id, image_path: p.image_path } : null))
            .catch(() => null)
        ).then((results) => {
          const map: Record<string, string> = {};
          results.forEach((r) => {
            if (r?.image_path) map[r.id] = r.image_path;
          });
          setProductIdToImage(map);
        });
      })
      .catch(() => setSupplierProducts([]))
      .finally(() => setLoading(false));
  }, [open, po.supplier_id, po.composition_snapshot, initialLines]);

  useEffect(() => {
    if (!open || kits.length === 0) return;
    const ids = [...new Set(kits.flatMap((k) => k.lines.map((ll) => ll.product_id).filter(Boolean)))];
    if (ids.length === 0) return;
    let cancelled = false;
    runInBatches(ids, PRODUCT_IMAGE_BATCH_SIZE, (id) =>
      getProduct(id)
        .then((p) => (p.image_path ? { id: p.id, image_path: p.image_path } : null))
        .catch(() => null)
    ).then((results) => {
      if (cancelled) return;
      setProductIdToImage((prev) => {
        const map = { ...prev };
        results.forEach((r) => {
          if (r?.image_path) map[r.id] = r.image_path;
        });
        return map;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, kits]);

  useEffect(() => {
    if (!open) return;
    const ids = new Set<string>();
    lines.forEach((l) => l.product_id && ids.add(l.product_id));
    kits.forEach((k) => k.lines.forEach((ll) => ll.product_id && ids.add(ll.product_id)));
    const uniqueIds = [...ids];
    if (uniqueIds.length === 0) {
      setSellingByProduct({});
      return;
    }
    let cancelled = false;
    fetchSellingAndNamesByProduct(uniqueIds).then(({ sellingByProduct: prices }) => {
      if (!cancelled) setSellingByProduct(prices);
    });
    return () => {
      cancelled = true;
    };
  }, [open, lines, kits]);

  const updateLine = (index: number, patch: Partial<EditLineState>) => {
    setLines((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const addLine = (sp: SupplierProduct) => {
    setLines((prev) => [
      ...prev,
      {
        product_id: sp.product_id,
        supplier_sku: sp.supplier_sku,
        description: sp.supplier_product_name ?? null,
        qty_ordered: 1,
        unit_cost: sp.unit_cost,
        currency: sp.currency ?? "CAD",
      },
    ]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const addKit = () => {
    setKits((prev) => [{ id: crypto.randomUUID(), name: `Kit ${prev.length + 1}`, quantity: 1, lines: [] }, ...prev]);
  };
  const updateKit = (kitId: string, patch: Partial<Omit<KitState, "id">>) => {
    setKits((prev) => prev.map((k) => (k.id === kitId ? { ...k, ...patch } : k)));
  };
  const removeKit = (kitId: string) => {
    setKits((prev) => prev.filter((k) => k.id !== kitId));
  };
  const addKitLine = async (kitId: string, sp: SupplierProduct, qtyPerKit: number) => {
    setKitExpanding(true);
    setError(null);
    try {
      const intQty = Math.max(1, Math.floor(Number(qtyPerKit)) || 1);
      const expanded = await expandProductToKitLines(sp.product_id, intQty);
      setKits((prev) =>
        prev.map((k) => (k.id === kitId ? { ...k, lines: mergeKitLines(k.lines, expanded) } : k))
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'ajout au kit");
    } finally {
      setKitExpanding(false);
    }
  };
  const removeKitLine = (kitId: string, lineIndex: number) => {
    setKits((prev) =>
      prev.map((k) => (k.id === kitId ? { ...k, lines: k.lines.filter((_, i) => i !== lineIndex) } : k))
    );
  };
  const updateKitLineQty = (kitId: string, lineIndex: number, qty_per_kit: number) => {
    const intQty = Math.max(1, Math.floor(Number(qty_per_kit)) || 1);
    setKits((prev) =>
      prev.map((k) => {
        if (k.id !== kitId) return k;
        const next = [...k.lines];
        if (next[lineIndex]) next[lineIndex] = { ...next[lineIndex], qty_per_kit: intQty };
        return { ...k, lines: next };
      })
    );
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const composition_snapshot =
        kits.length > 0
          ? {
              kits: kits.map((k) => ({
                name: k.name,
                quantity: k.quantity,
                lines: k.lines.map((l) => ({ product_id: l.product_id, qty_per_kit: l.qty_per_kit })),
              })),
              generated_at: new Date().toISOString(),
            }
          : po.composition_snapshot ?? undefined;
      await updatePurchaseOrder(poId, {
        supplier_reference: supplierReference || null,
        order_date: orderDate || null,
        expected_date: expectedDate || null,
        status,
        notes: notes || null,
        composition_snapshot: composition_snapshot ?? undefined,
      });
      for (const line of lines) {
        if (line.id) {
          await updatePurchaseOrderLine(poId, line.id, {
            qty_ordered: line.qty_ordered,
            unit_cost: line.unit_cost,
            currency: line.currency,
            description: line.description,
          });
        } else {
          await createPurchaseOrderLine(poId, {
            product_id: line.product_id,
            supplier_sku: line.supplier_sku,
            description: line.description,
            qty_ordered: line.qty_ordered,
            unit_cost: line.unit_cost,
            currency: line.currency,
          });
        }
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Modifier la commande" open={open} onClose={onClose} showDefaultButtons={false} showCloseButton size="4xl">
      <div className="space-y-5 min-h-[60vh]">
        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3 text-sm text-danger">{error}</div>
        )}
        <section>
          <h3 className="text-sm font-semibold text-text mb-2">Informations générales</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Référence / N° PO" value={supplierReference} onChange={(e) => setSupplierReference(e.target.value)} placeholder="Optionnel" />
            <Input label="Date commande" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
            <Input label="Date prévue" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-text mb-1">Statut</label>
              <select
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="draft">Brouillon</option>
                <option value="sent">Envoyée</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Notes</label>
              <textarea
                className="w-full min-h-[60px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                placeholder="Notes optionnelles"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text mb-2">Kits</h3>
          <p className="text-xs text-muted mb-2">
            Les kits permettent de structurer la commande par assemblage. Ils sont utilisés pour l’export Excel/PDF. Vous pouvez les modifier ici.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <Button label="+ Ajouter un kit" intent="default" size="sm" onClick={addKit} />
          </div>
          {kits.length > 0 && (
            <div className="space-y-4">
              {kits.map((kit) => (
                <div key={kit.id} className="rounded-lg border border-border bg-surface-muted/30 p-4">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <input
                      type="text"
                      className="flex-1 min-w-[120px] h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                      value={kit.name}
                      onChange={(e) => updateKit(kit.id, { name: e.target.value })}
                      placeholder="Nom du kit"
                    />
                    <label className="flex items-center gap-2 text-sm text-muted">
                      Quantité
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className="w-20 h-9 rounded-md border border-border bg-surface px-2 text-sm text-right text-text"
                        value={kit.quantity}
                        onChange={(e) => updateKit(kit.id, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                      />
                    </label>
                    <button
                      type="button"
                      className="text-danger hover:underline text-sm"
                      onClick={() => removeKit(kit.id)}
                    >
                      Supprimer le kit
                    </button>
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs text-muted mb-1">Ajouter un article au kit</label>
                    <ProductPicker
                      products={supplierProducts}
                      productIdToImage={productIdToImage}
                      placeholder="Rechercher un produit…"
                      disabled={loading || kitExpanding}
                      onSelect={(sp, q) => void addKitLine(kit.id, sp, q ?? 1)}
                      showQty
                    />
                  </div>
                  <div className="mb-2">
                    <BatchSkuAdd
                      products={supplierProducts}
                      onAdd={(sp, qty) => addKitLine(kit.id, sp, qty)}
                      disabled={loading || kitExpanding}
                      placeholder="Un SKU par ligne. Optionnel : SKU puis quantité (ex. BC.AG.SS001 2)"
                    />
                  </div>
                  {kitExpanding && <p className="text-xs text-muted mb-2">Décomposition du bundle…</p>}
                  {kit.lines.length > 0 ? (
                    <table className="w-full text-sm border border-border rounded overflow-hidden">
                      <thead>
                        <tr className="bg-surface-muted border-b border-border">
                          <th className="text-left p-2 font-medium">Nom / SKU</th>
                          <th className="text-right p-2 font-medium">Prix achat</th>
                          <th className="text-right p-2 font-medium">Prix détail</th>
                          <th className="text-right p-2 font-medium w-24">Qté/kit</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {kit.lines.map((kl, idx) => {
                          const sp = supplierProducts.find((p) => p.product_id === kl.product_id);
                          const consolidatedLine = lines.find((l) => l.product_id === kl.product_id);
                          const displayName = sp
                            ? (sp.product_name || sp.supplier_product_name || sp.supplier_sku || "—").trim() || "—"
                            : "—";
                          const displaySku = sp?.supplier_sku ?? kl.product_id.slice(0, 8);
                          const currency = sp?.currency ?? consolidatedLine?.currency ?? "CAD";
                          const purchasePrice = sp?.unit_cost ?? consolidatedLine?.unit_cost;
                          return (
                            <tr key={`${kit.id}-${idx}`} className="border-b border-border last:border-0">
                              <td className="p-2">{displayName} · {displaySku}</td>
                              <td className="p-2 text-right">{formatOrderPrice(purchasePrice, currency)}</td>
                              <td className="p-2 text-right">{formatOrderPrice(sellingByProduct[kl.product_id], currency)}</td>
                              <td className="p-2 text-right">
                                <input
                                  type="number"
                                  min={1}
                                  step={1}
                                  className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                                  value={kl.qty_per_kit}
                                  onChange={(e) => updateKitLineQty(kit.id, idx, Math.max(1, parseInt(e.target.value, 10) || 1))}
                                />
                              </td>
                              <td className="p-2">
                                <button
                                  type="button"
                                  className="text-danger hover:underline"
                                  onClick={() => removeKitLine(kit.id, idx)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-muted py-2">Aucun article dans ce kit. Ajoutez-en avec le sélecteur ci-dessus.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text mb-2">Lignes</h3>
          {consolidatedLoading && <p className="text-xs text-muted mb-2">Calcul des lignes à partir des kits…</p>}
          <div className="mb-2">
            <label className="block text-sm text-muted mb-1">Ajouter une ligne</label>
            <ProductPicker
              products={supplierProducts}
              productIdToImage={productIdToImage}
              placeholder="Rechercher un produit…"
              disabled={loading}
              onSelect={addLine}
            />
          </div>
          <div className="rounded border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-muted border-b border-border">
                  <th className="w-14 p-2 font-medium"></th>
                  <th className="text-left p-2 font-medium">Nom</th>
                  <th className="text-left p-2 font-medium">SKU</th>
                  <th className="text-right p-2 font-medium">Qté</th>
                  <th className="text-right p-2 font-medium">Prix achat</th>
                  <th className="text-right p-2 font-medium">Prix détail</th>
                  <th className="text-right p-2 font-medium">Total</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const total = line.qty_ordered * line.unit_cost;
                  return (
                    <tr key={line.id ?? i} className="border-b border-border last:border-0">
                      <td className="p-2 align-middle">
                        <ProductThumb imagePath={productIdToImage[line.product_id]} className="w-9 h-9" />
                      </td>
                      <td className="p-2">{line.description ?? "—"}</td>
                      <td className="p-2">{line.supplier_sku}</td>
                      <td className="p-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                          value={line.qty_ordered}
                          onChange={(e) => updateLine(i, { qty_ordered: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          className="w-24 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                          value={line.unit_cost}
                          onChange={(e) => updateLine(i, { unit_cost: parseFloat(e.target.value) || 0 })}
                        />
                      </td>
                      <td className="p-2 text-right">{formatOrderPrice(sellingByProduct[line.product_id], line.currency)}</td>
                      <td className="p-2 text-right">{total.toFixed(2)} {line.currency}</td>
                      <td className="p-2">
                        {!line.id && (
                          <button type="button" className="text-danger hover:underline text-xs" onClick={() => removeLine(i)}>Retirer</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={submitting} />
          <Button label={submitting ? "Enregistrement…" : "Enregistrer"} intent="primary" size="sm" onClick={handleSave} disabled={submitting} />
        </div>
      </div>
    </Modal>
  );
}
